/**
 * NNHC Clinical Portal — Phase 2 Forms
 * Wound Care Note | VNS/Seizure Log | IV Infusion Record | Vent/Trach Care | Skilled Nursing Assessment
 *
 * Compliance: ACHC HC1–HC7 · NC BON 21 NCAC 36 · 42 CFR §484 · NPAUP 2019 · AAN Seizure Standards
 * Signatures: ESIGN Act 15 U.S.C. §7001 · NC UETA G.S. 66-317
 */

import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useIsNarrow, useIsPortrait } from "./useResponsive";
import PhotoUpload from "./components/PhotoUpload";
import AnatomicalDiagram from "./components/AnatomicalDiagram";
import Icd10Search from "./components/Icd10Search";
import DocumentUpload from "./components/DocumentUpload";

// ─── Shared Design Tokens ─────────────────────────────────────────────────────
const C = {
  purple: "#4A1A7C", purpleLight: "#6B2FB5", purpleDark: "#2D0F4E",
  gold: "#C9922A", goldPale: "#FDF3E0",
  success: "#059669", warning: "#D97706", error: "#DC2626", info: "#0891B2",
  white: "#FFFFFF", offWhite: "#F8F6FB",
  g100: "#F3F4F6", g200: "#E5E7EB", g400: "#9CA3AF", g600: "#4B5563", g800: "#1F2937",
};

// ─── Shared UI Primitives ─────────────────────────────────────────────────────
const SH = ({ t, i, color }) => (
  <div style={{ background: `linear-gradient(90deg, ${(color||C.purple)}18, transparent)`,
    borderLeft: `4px solid ${color||C.purple}`, padding:"10px 16px",
    borderRadius:"0 8px 8px 0", margin:"24px 0 16px", fontWeight:700,
    color: color||C.purple, fontSize:13, letterSpacing:"0.05em", textTransform:"uppercase" }}>
    {i && <span style={{marginRight:8}}>{i}</span>}{t}
  </div>
);

const F = ({ label, req, children, inline, mic }) => (
  <div style={{ marginBottom:16, ...(inline ? {display:"inline-block", marginRight:16} : {}) }}>
    <label style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      fontSize:11, fontWeight:700, color:C.g600,
      marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>
      <span>{label}{req && <span style={{color:C.error,marginLeft:3}}>*</span>}</span>
      {mic && <MicHint targetId={mic} />}
    </label>
    {children}
  </div>
);

export const MicHint = ({ targetId }) => (
  <button
    type="button"
    title="Use your phone keyboard's mic button to dictate"
    aria-label="Tap 🎙️ then use your keyboard mic key to dictate"
    onClick={() => {
      const el = document.getElementById(targetId);
      if (el) el.focus();
    }}
    style={{
      background: 'none',
      border: '1px solid #ccc',
      borderRadius: '50%',
      width: 44,
      height: 44,
      cursor: 'pointer',
      fontSize: 18,
      marginLeft: 8,
      verticalAlign: 'middle',
      lineHeight: 1,
      padding: 0,
      flexShrink: 0,
    }}
  >🎙️</button>
);

const inp = {
  width:"100%", padding:"11px 14px", minHeight:44, border:`1.5px solid ${C.g200}`,
  borderRadius:8, fontSize:16, color:C.g800, fontFamily:"DM Sans, sans-serif",
  background:C.white, boxSizing:"border-box", outline:"none",
};

const G2 = ({children,gap}) => <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:gap||16,marginBottom:16}}>{children}</div>;
const G3 = ({children,gap}) => <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:gap||16,marginBottom:16}}>{children}</div>;
const G4 = ({children,gap}) => <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:gap||12,marginBottom:16}}>{children}</div>;

const Notice = ({ type, children }) => {
  const colors = { gold:[C.goldPale,C.gold], green:["#F0FDF4",C.success], red:["#FEF2F2",C.error], blue:["#EFF6FF",C.info] };
  const [bg, border] = colors[type]||colors.gold;
  return <div style={{ background:bg, border:`1.5px solid ${border}`, borderRadius:10,
    padding:"14px 16px", fontSize:13, lineHeight:1.6, marginBottom:20, color:C.g800 }}>{children}</div>;
};

const Steps = ({ steps, current, onGo }) => (
  <div style={{ display:"flex", gap:4, marginBottom:28 }}>
    {steps.map((s,i) => (
      <div key={i} onClick={()=>i<current&&onGo(i+1)} style={{
        flex:1, padding:"8px 4px", textAlign:"center", fontSize:10, fontWeight:700,
        borderRadius:8, cursor:i<current?"pointer":"default",
        background: current===i+1 ? C.purple : current>i+1 ? C.success : C.g100,
        color: current===i+1||current>i+1 ? C.white : C.g400,
        textTransform:"uppercase", letterSpacing:"0.05em", lineHeight:1.3
      }}>{current>i+1?"✓ ":""}{s}</div>
    ))}
  </div>
);

const BtnRow = ({ onBack, onNext, nextLabel, nextStyle, disabled }) => (
  <div style={{ display:"flex", justifyContent:"space-between", marginTop:24 }}>
    {onBack ? <button onClick={onBack} style={btnSec}>← Back</button> : <div/>}
    <button onClick={onNext} disabled={disabled} style={{ ...(nextStyle||btnPri), opacity:disabled?0.45:1 }}>
      {nextLabel||"Continue →"}
    </button>
  </div>
);

const btnPri = {
  background:`linear-gradient(135deg,${C.purple},${C.purpleLight})`, color:C.white,
  border:"none", borderRadius:8, padding:"12px 22px", minHeight:44, fontSize:14, fontWeight:600,
  cursor:"pointer", fontFamily:"Comfortaa, sans-serif", boxShadow:`0 2px 8px ${C.purple}30`,
};
const btnSec = {
  background:C.white, color:C.purple, border:`1.5px solid ${C.purple}`,
  borderRadius:8, padding:"11px 20px", minHeight:44, fontSize:13, fontWeight:600,
  cursor:"pointer", fontFamily:"Comfortaa, sans-serif",
};
const btnGold = {
  background:`linear-gradient(135deg,${C.gold},#E8B44A)`, color:C.white,
  border:"none", borderRadius:8, padding:"12px 22px", minHeight:44, fontSize:14, fontWeight:600,
  cursor:"pointer", fontFamily:"Comfortaa, sans-serif", boxShadow:`0 2px 8px ${C.gold}40`,
};

// Save & Continue Later — top-right button shown on every step except Signatures.
// onSave is called with no args; the parent owns the upsert/redirect logic.
export const SaveContinueButton = ({ onSave }) => (
  <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
    <button
      type="button"
      onClick={onSave}
      style={{
        background:C.white, color:C.purple, border:`1.5px solid ${C.purple}`,
        borderRadius:8, padding:"10px 16px", minHeight:44, fontSize:13, fontWeight:600,
        cursor:"pointer", fontFamily:"Comfortaa, sans-serif",
      }}
    >💾 Save &amp; Continue Later</button>
  </div>
);

const Chip = ({ label, active, onClick, color }) => (
  <div onClick={onClick} style={{
    padding:"7px 14px", borderRadius:20, cursor:"pointer", fontSize:13,
    fontWeight:500, transition:"all 0.15s",
    background: active ? (color||C.purple) : C.g100,
    color: active ? C.white : C.g600,
    border: `1.5px solid ${active ? (color||C.purple) : C.g200}`,
  }}>{active ? "✓ " : ""}{label}</div>
);

const ChipGroup = ({ options, value, onChange, multi, color, style }) => (
  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16, ...(style||{}) }}>
    {options.map(o => (
      <Chip key={o} label={o} color={color}
        active={multi ? (value||[]).includes(o) : value===o}
        onClick={() => {
          if (multi) {
            const cur = value||[];
            onChange(cur.includes(o) ? cur.filter(x=>x!==o) : [...cur,o]);
          } else { onChange(o); }
        }} />
    ))}
  </div>
);

const YNSelect = ({ value, onChange }) => {
  const norm = String(value||"").toUpperCase();
  return (
    <div style={{ display:"flex", gap:10 }}>
      {["YES","NO"].map(v => (
        <label key={v} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer",
          padding:"7px 14px", borderRadius:7, fontSize:13, fontWeight:600,
          background: norm===v ? (v==="YES"?"#F0FDF4":"#FFF1F0") : C.g100,
          border:`1.5px solid ${norm===v?(v==="YES"?C.success:C.error):C.g200}`,
          color: norm===v ? (v==="YES"?C.success:C.error) : C.g400,
          transition:"all 0.15s" }}>
          <input type="radio" name={`yn_${Math.random()}`} value={v} checked={norm===v}
            onChange={()=>onChange(v)} style={{display:"none"}} />
          {v}
        </label>
      ))}
    </div>
  );
};

// ─── Signature Pad ────────────────────────────────────────────────────────────
// forwardRef exposes getCurrentDataUrl() and hasStroke() — see ClinicalPortal
// SignaturePad for rationale (FIX 1 + FIX 2).
const SigPad = forwardRef(function SigPad({ label, signed, onSigned }, fwdRef) {
  const isNarrow = useIsNarrow();
  const isPortrait = useIsPortrait();
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [has, setHas] = useState(false);
  const last = useRef(null);

  useImperativeHandle(fwdRef, () => ({
    getCurrentDataUrl: () => {
      if (signed) return signed.url || signed.dataUrl || null;
      if (has && ref.current) return ref.current.toDataURL();
      return null;
    },
    hasStroke: () => !!signed || has,
  }), [signed, has]);
  const gp = (e,c) => {
    const r = c.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x:(cx-r.left)*c.width/r.width, y:(cy-r.top)*c.height/r.height };
  };
  const start = e => { e.preventDefault(); setDrawing(true); last.current = gp(e,ref.current); };
  const move = e => {
    e.preventDefault(); if (!drawing) return;
    const c=ref.current, ctx=c.getContext("2d"), p=gp(e,c);
    ctx.beginPath(); ctx.moveTo(last.current.x,last.current.y);
    ctx.lineTo(p.x,p.y); ctx.strokeStyle=C.purpleDark;
    ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.stroke();
    last.current=p; setHas(true);
  };
  const stop = () => setDrawing(false);
  const clear = () => { ref.current.getContext("2d").clearRect(0,0,520,90); setHas(false); onSigned(null); };
  const accept = () => onSigned({ url:ref.current.toDataURL(), ts:new Date().toISOString(), label });
  return (
    <div style={{ border:`2px solid ${signed?C.success:C.g200}`, borderRadius:12,
      padding:16, background: signed?"#F0FDF4":C.white, transition:"all 0.2s" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <span style={{ fontSize:11, fontWeight:700, color:C.g600, letterSpacing:"0.06em", textTransform:"uppercase" }}>{label}</span>
        {signed && <span style={{ fontSize:12, color:C.success, fontWeight:600 }}>✓ SIGNED {new Date(signed.ts).toLocaleTimeString()}</span>}
      </div>
      {!signed ? <>
        {isNarrow && isPortrait && (
          <div style={{ background:"#FEF3C7", border:`1.5px solid ${C.gold}`, borderRadius:8,
            padding:"10px 12px", fontSize:13, color:C.g800, marginBottom:10, lineHeight:1.5 }}>
            📱 Please rotate your device to landscape to sign more comfortably.
          </div>
        )}
        <canvas ref={ref} width={520} height={90}
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
          style={{ width:"100%", height:90, background:"#FAFAFA",
            border:`1.5px dashed ${C.g200}`, borderRadius:8, cursor:"crosshair",
            display:"block", touchAction:"none" }} />
        <div style={{ display:"flex", gap:8, marginTop:10 }}>
          <button onClick={clear} style={{ ...btnSec, padding:"7px 14px", fontSize:12 }}>Clear</button>
          <button onClick={accept} disabled={!has} style={{ ...btnPri, padding:"7px 14px", fontSize:12, opacity:has?1:0.45 }}>Accept</button>
        </div>
      </> : <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <img src={signed.url} alt="sig" style={{ height:55, maxWidth:260, background:"#FAFAFA",
          border:`1px solid ${C.g200}`, borderRadius:6 }} />
        <button onClick={()=>onSigned(null)} style={{ ...btnSec, padding:"6px 12px", fontSize:12 }}>Re-sign</button>
      </div>}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// FORM 1: WOUND CARE NOTE
// Based on: NNHC PRESSURE ULCER SKIN BREAKDOWN policies + WOUND_MGMT + NPAUP 2019
// ─────────────────────────────────────────────────────────────────────────────
const WOUND_LOCATION_OPTIONS = [
  "Sacrum / Coccyx",
  "Right Heel", "Left Heel",
  "Right Lateral Malleolus", "Left Lateral Malleolus",
  "Right Medial Malleolus", "Left Medial Malleolus",
  "Right Trochanter (Hip)", "Left Trochanter (Hip)",
  "Right Ischium (Buttock)", "Left Ischium (Buttock)",
  "Right Scapula", "Left Scapula",
  "Right Elbow", "Left Elbow",
  "Right Knee", "Left Knee",
  "Occipital", "Ear",
  "Surgical Incision Site",
  "Other",
];

// WoundCareForm step map (1-indexed):
//   1 — Wound Assessment       (per-wound entry, ICD-10, primary classification,
//                               + body-chart marker mapping at bottom — FIX 3)
//   2 — Tissue & Drainage      (wound bed composition, edges, periwound, odor)
//   3 — Treatment              (cleansing, dressing, narrative, photos)
//   4 — Signatures             (RN + patient/client; submit)
export function WoundCareForm({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [step, setStep] = useState(() => {
    const raw = parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1;
    // Drafts saved before FIX 3 may have lastStep=4 (old Anatomical Diagram)
    // or lastStep=5 (old Signatures). Collapse both into the new step 4.
    return raw >= 4 ? 4 : raw;
  });
  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const [woundPhoto, setWoundPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [bodyChartOpen, setBodyChartOpen] = useState(true);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke = rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"WOUND_CARE", data:d, lastStep:step, draftIdRef });
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setWoundPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };
  const [d, setD] = useState(() => ({
    visitDate: new Date().toISOString().split("T")[0],
    visitType: "Routine", woundCount: "1",
    // Wound 1
    woundLocation: "", woundSide: "",
    woundType: "", woundStage: "",
    lengthCm: "", widthCm: "", depthCm: "",
    undermining: "No", underminingDepth: "", underminingClock: "",
    tunneling: "No", tunnelingDepth: "", tunnelingClock: "",
    // Tissue
    pctNecrotic: "", pctSlough: "", pctGranulation: "", pctEpithelial: "",
    woundBed: "", exudateAmount: "", exudateType: [],
    woundEdges: [], periwound: [], odor: "None",
    // Pain
    painScale: "0", painQuality: "",
    // Treatment
    prevDressingCondition: "", prevDressingRemoved: "",
    cleansingAgent: "", cleansingMethod: "",
    currentDressing: [], dressingSizeFrequency: "",
    specialTreatments: [],
    photosTaken: "No", woundHealing: "",
    // Additional wounds
    additionalWounds: [],
    // Plan
    planChanges: "No", planNotes: "", mdNotified: "No", mdNotifiedTime: "", mdInstructions: "",
    narrative: "",
    // Anatomical diagram + multi-photo paths
    woundMarkers: [],
    photoPaths: [],
    ...(initialDraft?.form_data || {}),
  }));
  const u = (k,v) => setD(p=>({...p,[k]:v}));

  const LOCATIONS = ["Coccyx/Sacrum","Left Heel","Right Heel","Left Greater Trochanter","Right Greater Trochanter",
    "Left Ischium","Right Ischium","Left Lateral Malleolus","Right Lateral Malleolus","Left Medial Malleolus",
    "Right Medial Malleolus","Left Knee","Right Knee","Left Elbow","Right Elbow","Occiput","Left Hip","Right Hip",
    "Abdomen/Stoma Site","Left Foot","Right Foot","Left Lower Leg","Right Lower Leg","Other"];
  const WOUND_TYPES = ["Pressure Ulcer","Venous Insufficiency","Arterial/Ischemic","Diabetic Neuropathic",
    "Surgical Incision","Traumatic/Laceration","Skin Tear","Burn","Moisture-Associated","Other"];
  const STAGES = ["Stage I — Non-blanchable erythema, intact skin",
    "Stage II — Partial thickness, shallow open ulcer",
    "Stage III — Full thickness, subcutaneous visible",
    "Stage IV — Full thickness, bone/tendon/muscle exposed",
    "Unstageable — Wound bed obscured by eschar/slough",
    "Deep Tissue Injury (DTI) — Purple/maroon discoloration",
    "N/A — Non-pressure wound"];
  const WOUND_BED = ["Dry","Moist","Wet","Macerated","Friable","Beefy Red","Pale/Avascular"];
  const EXUDATE_AMOUNT = ["None","Scant","Small","Moderate","Large","Copious"];
  const EXUDATE_TYPE = ["Serous","Serosanguinous","Sanguinous","Purulent","Fibrinous"];
  const WOUND_EDGES = ["Well-defined","Rolled/Epibole","Macerated","Hyperkeratotic","Attached","Undermined","Irregular"];
  const PERIWOUND = ["Intact","Erythema (<2cm)","Erythema (>2cm — possible infection)","Induration","Edema",
    "Maceration","Dry/Flaking","Callus","Excoriation","Hyperpigmentation"];
  const DRESSINGS = ["Dry gauze","Wet-to-dry gauze","Non-adherent pad","Hydrocolloid","Hydrogel","Foam",
    "Transparent film","Alginate","CMC fiber","Composite","Collagen","Silver-impregnated",
    "NPWT (wound vac)","Compression wrap","Telfa","ABD pad","Other"];
  const SPECIAL = ["Debridement performed","Culture obtained","NPWT initiated","Compression initiated",
    "Wound vac canister changed","Offloading device applied","Wound measurements photographed"];

  const healingStatus = () => {
    if (!d.pctGranulation && !d.pctEpithelial) return "Unable to determine";
    const g = parseFloat(d.pctGranulation)||0, e = parseFloat(d.pctEpithelial)||0, n = parseFloat(d.pctNecrotic)||0;
    if (e>50) return "Epithelializing — Healing well";
    if (g>60) return "Granulating — Progressing";
    if (n>50) return "Non-healing — Necrotic tissue predominant";
    return "Partial granulation — Monitor closely";
  };

  return (
    <div style={{ fontFamily:"DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 4 ? handleSaveDraftClick : () => onSaveDraft({ type:"WOUND_CARE", data:d, lastStep:step, draftIdRef })} />
      )}
      <Steps steps={["Wound Assessment","Tissue & Drainage","Treatment","Signatures"]} current={step} onGo={setStep} />

      {step===1 && <>
        <Notice type="gold">
          <strong>NPAUP 2019 Compliance:</strong> Document all wound characteristics at every visit.
          Measurements required: length (head-to-toe), width (hip-to-hip), depth (deepest point), undermining (clock face), and tunneling.
          Photo documentation recommended every 2 weeks and with significant changes per NNHC Wound Management Policy.
        </Notice>
        <G3>
          <F label="Visit Date" req><input type="date" value={d.visitDate} onChange={e=>u("visitDate",e.target.value)} style={inp} /></F>
          <F label="Visit Type" req>
            <select value={d.visitType} onChange={e=>u("visitType",e.target.value)} style={inp}>
              {["Routine","Initial Wound Assessment","Reassessment","Wound Deterioration","Wound Resolution"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Number of Wounds">
            <select value={d.woundCount} onChange={e=>u("woundCount",e.target.value)} style={inp}>
              {["1","2","3","4","5+"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>

        <SH t="Per-Wound Summary" i="🩹" />
        {Array.from({ length: parseInt(d.woundCount) || 1 }, (_, i) => {
          const n = i + 1;
          return (
            <div key={n} style={{
              border: `2px solid ${C.purple}`, borderRadius: 8,
              padding: 16, marginBottom: 16
            }}>
              <h4 style={{ color: C.purple, marginBottom: 12,
                marginTop: 0, fontFamily: 'Comfortaa, sans-serif' }}>
                Wound #{n}
              </h4>
              {(() => {
                const raw = d[`woundLocation_${n}`] || '';
                const isOther = raw.startsWith('Other - ') ||
                  (raw && !WOUND_LOCATION_OPTIONS.includes(raw));
                const selectVal = isOther ? 'Other' : raw;
                const otherText = isOther ? raw.replace(/^Other - ?/, '') : '';
                return (
                  <>
                    <F label="Location" req>
                      <select value={selectVal}
                        onChange={e => {
                          const v = e.target.value;
                          setD(prev => ({...prev,
                            [`woundLocation_${n}`]:
                              v === 'Other' ? `Other - ${otherText}` : v}));
                        }}
                        style={inp}>
                        <option value="">Select location...</option>
                        {WOUND_LOCATION_OPTIONS.map(loc =>
                          <option key={loc} value={loc}>{loc}</option>)}
                      </select>
                    </F>
                    {selectVal === 'Other' && (
                      <F label="Specify Other Location" req>
                        <input type="text" value={otherText}
                          onChange={e => setD(prev => ({...prev,
                            [`woundLocation_${n}`]:
                              `Other - ${e.target.value}`}))}
                          placeholder="Type the wound location"
                          style={inp} />
                      </F>
                    )}
                  </>
                );
              })()}
              <F label="Stage / Type" req>
                <select value={d[`woundStage_${n}`] || ''}
                  onChange={e => setD(prev => ({...prev,
                    [`woundStage_${n}`]: e.target.value}))}
                  style={inp}>
                  <option value="">Select stage...</option>
                  <option>Stage I — Non-blanchable erythema, intact skin</option>
                  <option>Stage II — Partial thickness skin loss</option>
                  <option>Stage III — Full thickness skin loss</option>
                  <option>Stage IV — Full thickness tissue loss</option>
                  <option>Unstageable — Obscured by slough/eschar</option>
                  <option>Deep Tissue Injury (DTI)</option>
                  <option>Surgical wound</option>
                  <option>Skin tear</option>
                  <option>Excoriation</option>
                  <option>Non-pressure wound</option>
                </select>
              </F>
              <G3>
                <F label="Length (cm)">
                  <input type="number" step="0.1"
                    value={d[`woundLength_${n}`] || ''}
                    onChange={e => setD(prev => ({...prev,
                      [`woundLength_${n}`]: e.target.value}))}
                    style={inp} />
                </F>
                <F label="Width (cm)">
                  <input type="number" step="0.1"
                    value={d[`woundWidth_${n}`] || ''}
                    onChange={e => setD(prev => ({...prev,
                      [`woundWidth_${n}`]: e.target.value}))}
                    style={inp} />
                </F>
                <F label="Depth (cm)">
                  <input type="number" step="0.1"
                    value={d[`woundDepth_${n}`] || ''}
                    onChange={e => setD(prev => ({...prev,
                      [`woundDepth_${n}`]: e.target.value}))}
                    style={inp} />
                </F>
              </G3>
              <F label="Tissue Type (select all present)">
                <div style={{ display:"grid",
                  gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))",
                  gap:8 }}>
                  {[
                    { key: "Granulation", label: "Granulation (red/pink, healthy)" },
                    { key: "Slough", label: "Slough (yellow/tan, moist)" },
                    { key: "Eschar", label: "Eschar (brown/black, dry)" },
                    { key: "Necrotic", label: "Necrotic tissue" },
                    { key: "Epithelial", label: "Epithelial (pink, resurfacing)" },
                    { key: "Hypergranulation", label: "Hypergranulation" },
                    { key: "Bone/Tendon Exposed", label: "Bone / tendon exposed" },
                  ].map(opt => {
                    const curr = d[`woundTissue_${n}`] || [];
                    const checked = curr.includes(opt.key);
                    return (
                      <label key={opt.key} style={{
                        display:"flex", alignItems:"center", gap:10,
                        cursor:"pointer", padding:"8px 12px", borderRadius:8,
                        border:`1.5px solid ${checked ? C.purple : C.g200}`,
                        background: checked ? "#F5F0FA" : C.white,
                        fontSize:13, transition:"all 0.15s" }}>
                        <input type="checkbox" checked={checked}
                          onChange={e => setD(prev => {
                            const list = prev[`woundTissue_${n}`] || [];
                            return { ...prev,
                              [`woundTissue_${n}`]: e.target.checked
                                ? [...list, opt.key]
                                : list.filter(x => x !== opt.key) };
                          })}
                          style={{ width:17, height:17, accentColor:C.purple }} />
                        <span style={{ color:C.g800 }}>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </F>
              <G2>
                <F label="Drainage Amount">
                  <select value={d[`woundDrainageAmt_${n}`] || ''}
                    onChange={e => {
                      const v = e.target.value;
                      setD(prev => ({...prev,
                        [`woundDrainageAmt_${n}`]: v,
                        ...(v === "None"
                          ? { [`woundDrainageType_${n}`]: "None" }
                          : {})
                      }));
                    }}
                    style={inp}>
                    <option value="">Select...</option>
                    {["None","Scant","Small","Moderate","Large","Copious"]
                      .map(v => <option key={v}>{v}</option>)}
                  </select>
                </F>
                <F label="Drainage Type">
                  <select value={d[`woundDrainageType_${n}`] || ''}
                    disabled={d[`woundDrainageAmt_${n}`] === "None"}
                    onChange={e => setD(prev => ({...prev,
                      [`woundDrainageType_${n}`]: e.target.value}))}
                    style={{
                      ...inp,
                      background: d[`woundDrainageAmt_${n}`] === "None"
                        ? C.g100 : C.white,
                      cursor: d[`woundDrainageAmt_${n}`] === "None"
                        ? "not-allowed" : "default"
                    }}>
                    <option value="">Select...</option>
                    <option>None</option>
                    <option>Serous (clear/watery)</option>
                    <option>Serosanguineous (pink/watery)</option>
                    <option>Sanguineous (bloody)</option>
                    <option>Purulent (yellow/green, infected)</option>
                    <option>Seropurulent (cloudy)</option>
                  </select>
                </F>
              </G2>
              <F label="Healing Trend" req>
                <select value={d[`woundHealing_${n}`] || ''}
                  onChange={e => setD(prev => ({...prev,
                    [`woundHealing_${n}`]: e.target.value}))}
                  style={inp}>
                  <option value="">Select...</option>
                  <option>Improving — Measurable progress</option>
                  <option>Stable — No significant change</option>
                  <option>Declining — Worsening despite treatment</option>
                  <option>New wound</option>
                </select>
              </F>
            </div>
          );
        })}

        <SH t="Wound Diagnoses (ICD-10)" i="🏥" />
        <Icd10Search
          value={d.woundDiagnoses || []}
          onChange={(v) => setD(prev => ({ ...prev, woundDiagnoses: v }))}
          placeholder="Search ICD-10 code or description (e.g. L89, ulcer)..."
        />

        <SH t="Primary Wound — Location & Classification" i="📍" />
        <G2>
          <F label="Wound Location" req>
            <select value={d.woundLocation} onChange={e=>u("woundLocation",e.target.value)} style={inp}>
              <option value="">Select location...</option>
              {LOCATIONS.map(l=><option key={l}>{l}</option>)}
            </select>
          </F>
          <F label="Wound Type" req>
            <select value={d.woundType} onChange={e=>u("woundType",e.target.value)} style={inp}>
              <option value="">Select type...</option>
              {WOUND_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </F>
        </G2>

        {d.woundType === "Pressure Ulcer" && <>
          <F label="NPAUP Pressure Ulcer Stage" req>
            <select value={d.woundStage} onChange={e=>u("woundStage",e.target.value)} style={inp}>
              <option value="">Select stage...</option>
              {STAGES.map(s=><option key={s}>{s}</option>)}
            </select>
          </F>
          {d.woundStage && (
            <div style={{ background:`${C.info}10`, border:`1px solid ${C.info}40`, borderRadius:8,
              padding:"10px 14px", fontSize:12, color:C.g800, marginBottom:16, lineHeight:1.6 }}>
              <strong>Stage Definition:</strong> {d.woundStage.split("—")[1]?.trim()}
            </div>
          )}
        </>}

        <SH t="Wound Measurements (cm)" i="📏" />
        <G4>
          <F label="Length (cm) *"><input type="text" value={d.lengthCm} onChange={e=>u("lengthCm",e.target.value)} style={inp} placeholder="Head-to-toe axis" /></F>
          <F label="Width (cm) *"><input type="text" value={d.widthCm} onChange={e=>u("widthCm",e.target.value)} style={inp} placeholder="Hip-to-hip axis" /></F>
          <F label="Depth (cm) *"><input type="text" value={d.depthCm} onChange={e=>u("depthCm",e.target.value)} style={inp} placeholder="Deepest point" /></F>
          <F label="Area (cm²)">
            <input type="text" value={d.lengthCm && d.widthCm ? (parseFloat(d.lengthCm)*parseFloat(d.widthCm)).toFixed(2) : ""} readOnly style={{ ...inp, background:C.g100 }} />
          </F>
        </G4>

        <G2>
          <div>
            <F label="Undermining">
              <YNSelect value={d.undermining} onChange={v=>u("undermining",v)} />
            </F>
            {d.undermining==="YES" && <G2><F label="Depth (cm)"><input type="text" value={d.underminingDepth} onChange={e=>u("underminingDepth",e.target.value)} style={inp} /></F><F label="Clock Location"><input type="text" value={d.underminingClock} onChange={e=>u("underminingClock",e.target.value)} placeholder="e.g. 2 o'clock to 5 o'clock" style={inp} /></F></G2>}
          </div>
          <div>
            <F label="Tunneling/Sinus Tract">
              <YNSelect value={d.tunneling} onChange={v=>u("tunneling",v)} />
            </F>
            {d.tunneling==="YES" && <G2><F label="Depth (cm)"><input type="text" value={d.tunnelingDepth} onChange={e=>u("tunnelingDepth",e.target.value)} style={inp} /></F><F label="Direction"><input type="text" value={d.tunnelingClock} onChange={e=>u("tunnelingClock",e.target.value)} placeholder="e.g. 12 o'clock, 3 cm" style={inp} /></F></G2>}
          </div>
        </G2>

        <SH t="Pain Assessment" i="😣" />
        <G2>
          <F label="Pain Scale (0–10, with 0 = no pain)" req>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {[...Array(11)].map((_,i) => (
                <div key={i} onClick={()=>u("painScale",String(i))} style={{
                  width:34, height:34, borderRadius:8, display:"flex", alignItems:"center",
                  justifyContent:"center", cursor:"pointer", fontWeight:700, fontSize:13,
                  background: d.painScale===String(i) ? (i<=3?C.success:i<=6?C.warning:C.error) : C.g100,
                  color: d.painScale===String(i) ? C.white : C.g600,
                  border:`1.5px solid ${d.painScale===String(i)?(i<=3?C.success:i<=6?C.warning:C.error):C.g200}`,
                }}>{i}</div>
              ))}
            </div>
          </F>
          <F label="Pain Quality (patient's own words)">
            <input type="text" value={d.painQuality} onChange={e=>u("painQuality",e.target.value)} style={inp} placeholder="e.g. burning, aching, throbbing..." />
          </F>
        </G2>

        {/* FIX 3 — body chart embedded in Wound Assessment step */}
        <div style={{ border:`1.5px solid ${C.g200}`, borderRadius:10, marginTop:16, marginBottom:8, background:C.white, overflow:"hidden" }}>
          <button type="button" onClick={()=>setBodyChartOpen(o=>!o)}
            style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"12px 16px", background:"#F8F6FB", border:"none", cursor:"pointer",
              fontSize:13, fontWeight:700, color:C.purpleDark, letterSpacing:"0.04em", textTransform:"uppercase" }}>
            <span>{bodyChartOpen ? "▼" : "▶"}  Mark wound location on body chart</span>
            <span style={{ fontSize:11, color:C.g600, fontWeight:500, textTransform:"none", letterSpacing:0 }}>
              {(d.woundMarkers || []).length} marker{(d.woundMarkers || []).length === 1 ? "" : "s"} placed
            </span>
          </button>
          {bodyChartOpen && (
            <div style={{ padding:16 }}>
              <p style={{ fontSize:12, color:C.g600, marginTop:0, marginBottom:12 }}>
                Tap the body diagram to place a marker for each wound.
              </p>
              <AnatomicalDiagram
                wounds={Array.from(
                  { length: parseInt(d.woundCount) || 1 },
                  (_, i) => ({
                    woundNumber: i + 1,
                    location: d[`woundLocation_${i+1}`] || (i === 0 ? d.woundLocation : '') || `Wound ${i+1}`,
                    stage: d[`woundStage_${i+1}`] || (i === 0 ? d.woundStage : '') || ''
                  })
                )}
                markers={d.woundMarkers || []}
                onChange={(markers) =>
                  setD(prev => ({ ...prev, woundMarkers: markers }))}
              />
            </div>
          )}
        </div>

        <BtnRow onNext={()=>setStep(step + 1)} />
      </>}

      {step===2 && <>
        <SH t="Wound Bed — Tissue Composition" i="🔬" />
        <Notice type="blue">
          Document percentage of each tissue type. Total should equal 100%. Per NNHC policy: tissue color
          progresses from black (necrotic) → yellow (slough) → red (granulation) → pink (epithelial) with healing.
        </Notice>
        <G4>
          {[["pctNecrotic","% Necrotic (Black/Eschar)"],["pctSlough","% Slough (Yellow/Fibrinous)"],
            ["pctGranulation","% Granulation (Red/Beefy)"],["pctEpithelial","% Epithelial (Pink/New)"]].map(([k,l])=>(
            <F key={k} label={l}>
              <input type="number" min="0" max="100" value={d[k]} onChange={e=>u(k,e.target.value)} style={inp} placeholder="0–100" />
            </F>
          ))}
        </G4>

        {d.pctNecrotic && d.pctSlough && d.pctGranulation && d.pctEpithelial && (
          <div style={{ background:"#F8F6FB", border:`1px solid ${C.purple}30`, borderRadius:8,
            padding:"10px 14px", fontSize:12, marginBottom:16 }}>
            <strong>Healing Status Interpretation:</strong> {healingStatus()}
            {" "} | <strong>Total:</strong> {[d.pctNecrotic,d.pctSlough,d.pctGranulation,d.pctEpithelial].reduce((a,v)=>a+(parseFloat(v)||0),0)}%
          </div>
        )}

        <G2>
          <F label="Wound Bed Moisture" req>
            <ChipGroup options={WOUND_BED} value={d.woundBed} onChange={v=>u("woundBed",v)} />
          </F>
          <F label="Exudate Amount" req>
            <ChipGroup options={EXUDATE_AMOUNT} value={d.exudateAmount} onChange={v=>u("exudateAmount",v)} />
          </F>
        </G2>

        <F label="Exudate Type (select all that apply)">
          <ChipGroup options={EXUDATE_TYPE} value={d.exudateType} onChange={v=>u("exudateType",v)} multi />
        </F>

        <SH t="Wound Edges & Periwound Skin" i="🔍" />
        <G2>
          <F label="Wound Edges (select all that apply)">
            <ChipGroup options={WOUND_EDGES} value={d.woundEdges} onChange={v=>u("woundEdges",v)} multi color={C.warning} />
          </F>
          <F label="Periwound Skin Condition (select all that apply)">
            <ChipGroup options={PERIWOUND} value={d.periwound} onChange={v=>u("periwound",v)} multi color={C.info} />
          </F>
        </G2>

        <F label="Wound Odor">
          <ChipGroup options={["None","Mild","Moderate","Strong — Possible Infection"]} value={d.odor}
            onChange={v=>u("odor",v)}
            color={d.odor==="None"?C.success:d.odor.includes("Infection")?C.error:C.warning} />
        </F>

        {d.odor && d.odor !== "None" && (
          <Notice type="red">
            <strong>⚠️ Odor Present:</strong> Moderate to strong odor may indicate infection. Consider: culture and sensitivity,
            wound debridement, antimicrobial dressing, and provider notification. Document periwound signs of cellulitis
            (erythema &gt;2cm, induration, warmth, streaking).
          </Notice>
        )}
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===3 && <>
        <SH t="Previous Dressing Assessment" i="🗑️" />
        <G2>
          <F label="Previous Dressing Removed">
            <input type="text" value={d.prevDressingRemoved} onChange={e=>u("prevDressingRemoved",e.target.value)}
              style={inp} placeholder="Type of previous dressing" />
          </F>
          <F label="Previous Dressing Condition">
            <select value={d.prevDressingCondition} onChange={e=>u("prevDressingCondition",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Intact — dry","Intact — saturated","Intact — partially saturated","Dislodged/displaced",
                "No dressing present","Adhered to wound bed"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G2>

        <SH t="Wound Cleansing" i="💧" />
        <G2>
          <F label="Cleansing Agent" req>
            <select value={d.cleansingAgent} onChange={e=>u("cleansingAgent",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Normal Saline (0.9% NaCl)","Wound cleanser (commercial)","CHG solution","Sterile water",
                "Dakin's solution","Povidone-iodine","Acetic acid","Hypochlorous acid (HOCl)"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Cleansing Method">
            <select value={d.cleansingMethod} onChange={e=>u("cleansingMethod",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Gentle irrigation with syringe","Pour rinse","Gentle wiping — inside to outside",
                "Pulsatile lavage","Wound vac irrigation","Shower"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G2>

        <SH t="Dressing Applied" i="🩹" />
        <F label="Primary Dressing (select all applied)" req>
          <ChipGroup options={DRESSINGS} value={d.currentDressing} onChange={v=>u("currentDressing",v)} multi color={C.purple} />
        </F>
        <G2>
          <F label="Dressing Size / Frequency of Change">
            <input type="text" value={d.dressingSizeFrequency} onChange={e=>u("dressingSizeFrequency",e.target.value)}
              style={inp} placeholder="e.g. 4x4 / Change every 3 days or PRN" />
          </F>
          <F label="Photos Taken?">
            <ChipGroup options={["Yes","No"]} value={d.photosTaken}
              onChange={v=>u("photosTaken",v)} color={C.success} />
            {d.photosTaken === "Yes" && (
              <div style={{ marginTop: 12, padding: 12, background: '#F5F0FA',
                borderRadius: 8 }}>
                <label style={{ fontWeight: 600, display: 'block',
                  marginBottom: 8 }}>Upload Wound Photo</label>
                <input type="file" accept="image/*" capture="environment"
                  onChange={handlePhotoSelect} />
                {photoPreview && (
                  <img src={photoPreview} alt="Wound" style={{
                    marginTop: 8, maxWidth: 200, maxHeight: 150,
                    borderRadius: 4, border: '1px solid #ccc' }} />
                )}
                {photoUploading && <p style={{ color: '#6B2D9E' }}>
                  Uploading photo...</p>}
              </div>
            )}
          </F>
        </G2>

        <F label="Special Treatments/Procedures (select all that apply)">
          <ChipGroup options={SPECIAL} value={d.specialTreatments} onChange={v=>u("specialTreatments",v)} multi color={C.info} />
        </F>

        <SH t="Wound Healing Trajectory" i="📈" />
        <G2>
          <F label="Overall Wound Status" req>
            <select value={d.woundHealing} onChange={e=>u("woundHealing",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Healing — Improved from last visit","Stable — No significant change","Deteriorating — Increased size/depth",
                "New wound — Initial documentation","Resolved/Closed","Referred to wound specialist"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <div>
            <F label="POC/Plan Changes Required?">
              <YNSelect value={d.planChanges} onChange={v=>u("planChanges",v)} />
            </F>
          </div>
        </G2>

        <SH t="Provider Notification" i="📞" />
        <G2>
          <div>
            <F label="MD/NP Notified?">
              <YNSelect value={d.mdNotified} onChange={v=>u("mdNotified",v)} />
            </F>
            {d.mdNotified==="YES" && <G2><F label="Time Notified"><input type="time" value={d.mdNotifiedTime} onChange={e=>u("mdNotifiedTime",e.target.value)} style={inp} /></F><F label="Instructions Given"><input type="text" value={d.mdInstructions} onChange={e=>u("mdInstructions",e.target.value)} style={inp} /></F></G2>}
          </div>
          <F label="Clinical Narrative" mic="wound-narrative">
            <textarea id="wound-narrative" value={d.narrative} onChange={e=>u("narrative",e.target.value)} style={{ ...inp, minHeight:80, resize:"vertical" }}
              placeholder="Wound progress, patient/caregiver education, response to treatment, care coordination..." />
          </F>
        </G2>

        <PhotoUpload
          docId={null}
          formType="WOUND_CARE"
          onUploaded={(paths) =>
            setD(prev => ({ ...prev, photoPaths: paths }))}
        />

        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===4 && <>
        <SH t="Signatures" i="✍️" />
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <SigPad ref={rnSigPadRef} label="RN / LPN CLINICIAN SIGNATURE" signed={rnSig} onSigned={setRnSig} />
          <SigPad ref={clientSigPadRef} label={<>PATIENT / CLIENT SIGNATURE <span style={{color:"#DC2626"}}>*</span></>}
            signed={clientSig} onSigned={setClientSig} />
        </div>
        {(!rnSig || !clientSig) && (
          <p style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>
            Both nurse and patient signatures required
          </p>
        )}
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>{
            const rn = sigFromPad(rnSig, rnSigPadRef, "RN / LPN CLINICIAN SIGNATURE");
            const client = sigFromPad(clientSig, clientSigPadRef, "PATIENT / CLIENT SIGNATURE");
            if (!rn || !client) return;
            setPhotoUploading(!!woundPhoto);
            onComplete({type:"WOUND_CARE",data:d,rnSig:rn,clientSig:client,patient,woundPhoto,draftIdRef});
          }}
          nextLabel="✓ Submit Wound Care Note" nextStyle={btnGold}
          disabled={!rnSig || !clientSig} />
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM 2: VNS / SEIZURE EVENT LOG
// Based on: AAN Seizure Documentation Standards + NC Medicaid Policy 3G PDN Requirements
// VNS Device: Cyberonics/LivaNova VNS Therapy documentation requirements
// ─────────────────────────────────────────────────────────────────────────────
export function SeizureVNSForm({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    if (rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.()) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"SEIZURE_VNS", data:d, lastStep:1, draftIdRef });
  };
  const [d, setD] = useState(() => ({
    eventDate: new Date().toISOString().split("T")[0],
    eventTime: new Date().toTimeString().slice(0,5),
    durationMin: "", durationSec: "",
    seizureType: "",
    precedingEvents: [], auraDescription: "",
    bodyPartsInvolved: [],
    lossOfConsciousness: "No",
    eyeMovement: "",
    skinColor: "",
    vnsDevice: "Yes",
    vnsModel: "", vnsSerialNo: "",
    magnetUsed: "No", magnetTime: "", magnetResponse: "",
    magnetSwipes: "1",
    rescueMedGiven: "No",
    rescueMed: "", rescueDose: "", rescueRoute: "", rescueTime: "", rescueResponse: "",
    // Post-ictal
    postIctalDuration: "", postIctalStatus: [],
    // Vitals post-seizure
    postBP: "", postPulse: "", postResp: "", postO2: "", postTemp: "",
    // Recovery
    recoveryTime: "", recoveryStatus: "",
    // Notification
    providerNotified: "No", providerTime: "", providerOrders: "",
    ems911: "No", emsTime: "", emsReason: "",
    caregiverNotified: "No", caregiverTime: "",
    // Context
    possibleTriggers: [],
    seizureFreqChange: "No change",
    previousSeizureDate: "",
    narrative: "",
    ...(initialDraft?.form_data || {}),
  }));
  const u = (k,v) => setD(p=>({...p,[k]:v}));

  const SEIZURE_TYPES = [
    "Tonic-Clonic (Grand Mal)","Tonic","Clonic","Atonic (Drop Attack)",
    "Myoclonic","Absence (Petit Mal)","Focal Aware (Simple Partial)",
    "Focal Impaired Awareness (Complex Partial)","Focal to Bilateral Tonic-Clonic",
    "Epileptic Spasm","Unknown/Unclassified"
  ];
  const PRECEDING = ["Aura reported","No warning/aura","Sleep","Waking from sleep","Fever","Emotional stress",
    "Lights/visual stimulation","Noise","Exercise","Missed medication","Illness/infection","Unknown"];
  const BODY_PARTS = ["Bilateral — full body","Left arm","Right arm","Left leg","Right leg",
    "Face — left","Face — right","Face — bilateral","Head turning","Eye deviation",
    "Trunk","Focal — left side","Focal — right side"];
  const POST_ICTAL = ["Confused","Drowsy/lethargic","Unresponsive","Agitated","Crying","Vomiting",
    "Headache","Weakness — left side","Weakness — right side","Normal baseline achieved","Slept"];
  const TRIGGERS = ["Missed medication dose","Fever/illness","Sleep deprivation","Emotional stress",
    "Sensory stimulus","Physical exertion","Unknown","No apparent trigger"];
  const RESCUE_ROUTES = ["Intranasal (IN)","Buccal","Rectal (PR)","Intramuscular (IM)","Oral","IV"];

  return (
    <div style={{ fontFamily:"DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={handleSaveDraftClick} />
      )}
      <Notice type="red">
        <strong>⚠️ AAN Documentation Requirement:</strong> Every seizure event must be documented within 24 hours
        with: event time, duration, seizure type, body involvement, LOC, post-ictal status, and any interventions.
        VNS magnet use and rescue medications must be documented with exact time. 911 threshold: seizure &gt;5 minutes,
        two consecutive seizures without recovery, or respiratory compromise (status epilepticus criteria).
      </Notice>

      <SH t="Event Details" i="⚡" />
      <G4>
        <F label="Date of Event" req><input type="date" value={d.eventDate} onChange={e=>u("eventDate",e.target.value)} style={inp} /></F>
        <F label="Time Onset" req><input type="time" value={d.eventTime} onChange={e=>u("eventTime",e.target.value)} style={inp} /></F>
        <F label="Duration (min)"><input type="number" value={d.durationMin} onChange={e=>u("durationMin",e.target.value)} style={inp} placeholder="0" min="0" /></F>
        <F label="Duration (sec)"><input type="number" value={d.durationSec} onChange={e=>u("durationSec",e.target.value)} style={inp} placeholder="0" min="0" max="59" /></F>
      </G4>

      {d.durationMin && parseFloat(d.durationMin) >= 5 && (
        <Notice type="red">
          <strong>🚨 STATUS EPILEPTICUS ALERT:</strong> Seizure duration ≥5 minutes. Per AAN guidelines,
          this meets criteria for status epilepticus. If not already resolved: administer rescue medication per orders,
          call 911 immediately, and notify provider. Document all actions with exact timestamps.
        </Notice>
      )}

      <F label="Seizure Type" req>
        <select value={d.seizureType} onChange={e=>u("seizureType",e.target.value)} style={inp}>
          <option value="">Select seizure type...</option>
          {SEIZURE_TYPES.map(t=><option key={t}>{t}</option>)}
        </select>
      </F>

      <G2>
        <F label="Preceding Events / Aura (select all that apply)">
          <ChipGroup options={PRECEDING} value={d.precedingEvents} onChange={v=>u("precedingEvents",v)} multi color={C.warning} />
        </F>
        <F label="Body Parts Involved (select all that apply)">
          <ChipGroup options={BODY_PARTS} value={d.bodyPartsInvolved} onChange={v=>u("bodyPartsInvolved",v)} multi color={C.purple} />
        </F>
      </G2>

      <G3>
        <F label="Loss of Consciousness?">
          <ChipGroup options={["Yes","No","Unable to determine"]} value={d.lossOfConsciousness} onChange={v=>u("lossOfConsciousness",v)} />
        </F>
        <F label="Eye Movement">
          <select value={d.eyeMovement} onChange={e=>u("eyeMovement",e.target.value)} style={inp}>
            <option value="">Select...</option>
            {["Deviated left","Deviated right","Deviated up","Nystagmus","Blinking","Staring","Closed","Not observed"].map(v=><option key={v}>{v}</option>)}
          </select>
        </F>
        <F label="Skin Color During Seizure">
          <select value={d.skinColor} onChange={e=>u("skinColor",e.target.value)} style={inp}>
            <option value="">Select...</option>
            {["Normal","Pale","Flushed/red","Cyanotic (blue)","Gray/mottled","Not observed"].map(v=><option key={v}>{v}</option>)}
          </select>
        </F>
      </G3>

      <SH t="VNS Device (Vagus Nerve Stimulator)" i="📡" />
      <G3>
        <F label="VNS Device Present?">
          <ChipGroup options={["Yes","No","Unknown"]} value={d.vnsDevice} onChange={v=>u("vnsDevice",v)} />
        </F>
        {d.vnsDevice==="Yes" && <>
          <F label="VNS Model"><input type="text" value={d.vnsModel} onChange={e=>u("vnsModel",e.target.value)} style={inp} placeholder="e.g. LivaNova Model 106" /></F>
          <F label="Serial/Device #"><input type="text" value={d.vnsSerialNo} onChange={e=>u("vnsSerialNo",e.target.value)} style={inp} /></F>
        </>}
      </G3>

      {d.vnsDevice==="Yes" && <>
        <G4>
          <F label="VNS Magnet Used?">
            <ChipGroup options={["Yes","No"]} value={d.magnetUsed} onChange={v=>u("magnetUsed",v)} />
          </F>
          {d.magnetUsed==="Yes" && <>
            <F label="Time of Magnet Swipe"><input type="time" value={d.magnetTime} onChange={e=>u("magnetTime",e.target.value)} style={inp} /></F>
            <F label="Number of Swipes">
              <select value={d.magnetSwipes} onChange={e=>u("magnetSwipes",e.target.value)} style={inp}>
                {["1","2","3","4","5+"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
            <F label="Response to Magnet Swipe">
              <select value={d.magnetResponse} onChange={e=>u("magnetResponse",e.target.value)} style={inp}>
                <option value="">Select...</option>
                {["Seizure aborted","Seizure shortened","No apparent effect","Gradual improvement"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
          </>}
        </G4>
      </>}

      <SH t="Rescue Medication" i="💊" />
      <F label="Rescue Medication Administered?">
        <ChipGroup options={["Yes","No"]} value={d.rescueMedGiven} onChange={v=>u("rescueMedGiven",v)} />
      </F>
      {d.rescueMedGiven==="Yes" && (
        <div style={{ background:"#FEF2F2", border:`1px solid ${C.error}30`, borderRadius:10, padding:16, marginBottom:16 }}>
          <G4>
            <F label="Medication Name" req><input type="text" value={d.rescueMed} onChange={e=>u("rescueMed",e.target.value)} style={inp} placeholder="e.g. Diastat, Klonopin, Versed" /></F>
            <F label="Dose" req><input type="text" value={d.rescueDose} onChange={e=>u("rescueDose",e.target.value)} style={inp} placeholder="e.g. 0.5 mg" /></F>
            <F label="Route" req>
              <select value={d.rescueRoute} onChange={e=>u("rescueRoute",e.target.value)} style={inp}>
                <option value="">Select route...</option>
                {RESCUE_ROUTES.map(r=><option key={r}>{r}</option>)}
              </select>
            </F>
            <F label="Time Administered" req><input type="time" value={d.rescueTime} onChange={e=>u("rescueTime",e.target.value)} style={inp} /></F>
          </G4>
          <F label="Patient Response to Rescue Medication">
            <select value={d.rescueResponse} onChange={e=>u("rescueResponse",e.target.value)} style={inp}>
              <option value="">Select response...</option>
              {["Seizure aborted within 5 minutes","Partial response — seizure shortened","No response — 911 called",
                "Delayed response — seizure resolved in >10 minutes","Adverse reaction noted"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </div>
      )}

      <SH t="Post-Ictal Assessment" i="🔄" />
      <G2>
        <F label="Post-Ictal Status (select all that apply)">
          <ChipGroup options={POST_ICTAL} value={d.postIctalStatus} onChange={v=>u("postIctalStatus",v)} multi color={C.warning} />
        </F>
        <G2>
          <F label="Post-Ictal Duration (min)"><input type="number" value={d.postIctalDuration} onChange={e=>u("postIctalDuration",e.target.value)} style={inp} /></F>
          <F label="Time to Baseline"><input type="time" value={d.recoveryTime} onChange={e=>u("recoveryTime",e.target.value)} style={inp} /></F>
        </G2>
      </G2>

      <SH t="Vital Signs Post-Seizure" i="🩺" />
      <G4>
        {[["postBP","B/P"],["postPulse","Pulse"],["postResp","Resp"],["postO2","O2 Sat %"],["postTemp","Temp"]].slice(0,4).map(([k,l])=>(
          <F key={k} label={l}><input type="text" value={d[k]} onChange={e=>u(k,e.target.value)} style={inp} /></F>
        ))}
      </G4>

      <SH t="Notifications & Emergency Response" i="📞" />
      <G3>
        <div>
          <F label="Provider Notified?"><YNSelect value={d.providerNotified} onChange={v=>u("providerNotified",v)} /></F>
          {d.providerNotified==="YES" && <>
            <F label="Time"><input type="time" value={d.providerTime} onChange={e=>u("providerTime",e.target.value)} style={inp} /></F>
            <F label="Orders Given"><input type="text" value={d.providerOrders} onChange={e=>u("providerOrders",e.target.value)} style={inp} /></F>
          </>}
        </div>
        <div>
          <F label="911 / EMS Called?"><YNSelect value={d.ems911} onChange={v=>u("ems911",v)} /></F>
          {d.ems911==="YES" && <>
            <F label="Time Called"><input type="time" value={d.emsTime} onChange={e=>u("emsTime",e.target.value)} style={inp} /></F>
            <F label="Reason / Disposition"><input type="text" value={d.emsReason} onChange={e=>u("emsReason",e.target.value)} style={inp} /></F>
          </>}
        </div>
        <div>
          <F label="Caregiver Notified?"><YNSelect value={d.caregiverNotified} onChange={v=>u("caregiverNotified",v)} /></F>
          {d.caregiverNotified==="YES" && <F label="Time"><input type="time" value={d.caregiverTime} onChange={e=>u("caregiverTime",e.target.value)} style={inp} /></F>}
        </div>
      </G3>

      <G2>
        <F label="Possible Triggers (select all that apply)">
          <ChipGroup options={TRIGGERS} value={d.possibleTriggers} onChange={v=>u("possibleTriggers",v)} multi color={C.warning} />
        </F>
        <div>
          <F label="Compared to Baseline Seizure Frequency">
            <ChipGroup options={["No change","Increased","Decreased","First recorded seizure"]}
              value={d.seizureFreqChange} onChange={v=>u("seizureFreqChange",v)} />
          </F>
          <F label="Date of Previous Seizure Event">
            <input type="date" value={d.previousSeizureDate} onChange={e=>u("previousSeizureDate",e.target.value)} style={inp} />
          </F>
        </div>
      </G2>

      <F label="Clinical Narrative" mic="seizure-narrative">
        <textarea id="seizure-narrative" value={d.narrative} onChange={e=>u("narrative",e.target.value)} style={{ ...inp, minHeight:100, resize:"vertical" }}
          placeholder="Describe sequence of events, nursing interventions, patient/caregiver response, education provided, and plan of care follow-up..." />
      </F>

      <PhotoUpload
        docId={null}
        formType="SEIZURE_VNS"
        onUploaded={(paths) =>
          setD(prev => ({ ...prev, photoPaths: paths }))}
      />

      <SH t="Clinician Signature" i="✍️" />
      <Notice type="gold">
        <strong>Documentation Timing Requirement:</strong> Seizure event log must be completed within 24 hours
        of the event per ACHC HC5-1A and NC Medicaid Policy 3G. If 911 was called, incident report must also
        be filed per NNHC policy within 24 hours.
      </Notice>
      <SigPad ref={rnSigPadRef} label="RN / LPN CLINICIAN SIGNATURE — SEIZURE EVENT LOG" signed={rnSig} onSigned={setRnSig} />
      <SigPad ref={clientSigPadRef} label="CLIENT / CAREGIVER SIGNATURE (OPTIONAL)" signed={clientSig} onSigned={setClientSig} />
      <div style={{ display:"flex", justifyContent:"flex-end", marginTop:20 }}>
        <button onClick={()=>{
            const rn = sigFromPad(rnSig, rnSigPadRef, "RN / LPN CLINICIAN SIGNATURE — SEIZURE EVENT LOG");
            const client = sigFromPad(clientSig, clientSigPadRef, "CLIENT / CAREGIVER SIGNATURE");
            if (!rn) return;
            onComplete({type:"SEIZURE_VNS",data:d,rnSig:rn,clientSig:client,patient,draftIdRef});
          }} disabled={!rnSig && !rnSigPadRef.current?.hasStroke?.()}
          style={{ ...btnGold, opacity:rnSig?1:0.45 }}>✓ Submit Seizure/VNS Log</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM 3: IV INFUSION RECORD
// Based on: NNHC IV THERAPY policies (Peripheral + CVC), INS 2021 Standards
// RN-only per NC BON scope
// ─────────────────────────────────────────────────────────────────────────────
export function IVInfusionForm({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const [rnSig, setRnSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    if (rnSigPadRef.current?.hasStroke?.()) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"IV_INFUSION", data:d, lastStep:step, draftIdRef });
  };
  const [d, setD] = useState(() => ({
    visitDate: new Date().toISOString().split("T")[0],
    ivType: "Peripheral",
    // Site
    siteLocation: "", siteArm: "", gauge: "", insertionDate: "", insertedBy: "",
    dressingSite: "", dressingCondition: "",
    // Assessment
    phlebitisScore: "0", infiltrationScore: "0",
    siteErythema: "No", sitePain: "No", siteEdema: "No",
    siteDrainage: "No", siteDrainageDesc: "",
    bloodReturn: "Yes",
    // CVC specific
    cvcType: "", cvcPort: "",
    heparinFlushPre: "No", heparinFlushPost: "No", salineFlushPre: "No", salineFlushPost: "No",
    flushVolume: "",
    // Infusion
    medication: "", concentration: "", rate: "", totalVolume: "",
    startTime: "", stopTime: "",
    infPumpUsed: "Yes", infPumpManuf: "", infPumpRate: "",
    // Meds given
    meds: [{ name:"", dose:"", route:"IV", time:"", response:"" }],
    // Complications
    complications: [], complicationNotes: "",
    // Change
    tubingChanged: "No", dressChanged: "No", capChanged: "No",
    tubingChangeDate: "", dressChangeDate: "",
    // Response
    patientTolerance: "", patientResponse: "",
    mdNotified: "No", mdReason: "",
    narrative: "",
    ...(initialDraft?.form_data || {}),
  }));
  const u = (k,v) => setD(p=>({...p,[k]:v}));

  const PHLEBITIS_SCALE = [
    ["0","No symptoms"],["1+","Erythema at access site ± pain"],["2+","Pain + erythema and/or edema"],
    ["3+","Pain + erythema/edema + streak + palpable venous cord"],
  ];
  const INFILTRATION_SCALE = [
    ["0","No symptoms"],["1","Blanched skin, edema <1 inch, cool to touch ± pain"],
    ["2","Blanched skin, edema 1–6 inches, cool, ± pain"],["3","Translucent skin, edema >6 inches, mild-moderate pain"],
    ["4","Translucent/tight skin, gross edema, circulatory compromise, severe pain"],
  ];
  const SITES_PERIPHERAL = ["Right forearm","Left forearm","Right antecubital","Left antecubital",
    "Right hand dorsum","Left hand dorsum","Right wrist","Left wrist"];
  const GAUGES = ["14g","16g","18g","20g","22g","24g"];
  const CVC_TYPES = ["PICC","Tunneled Hickman/Broviac","Groshong","Port-A-Cath (implanted)","Subclavian CVC","Jugular CVC","Midline"];
  const COMPLICATIONS = ["Site infiltration","Phlebitis","Occlusion/clot","Air in tubing","Leakage at connection",
    "Adverse drug reaction","Anaphylaxis signs","Pump alarm","Line dislodgement","Infection signs — site","Other"];

  return (
    <div style={{ fontFamily:"DM Sans, sans-serif" }}>
      <Notice type="red">
        <strong>NC BON Scope:</strong> IV infusion therapy is RN-only per NC G.S. 90-171.20.
        LPN may monitor running IVs under RN supervision but may not initiate or administer IV push medications.
        All IV complications scored ≥2+ (phlebitis) or ≥2 (infiltration) require immediate MD notification.
      </Notice>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 3 ? handleSaveDraftClick : () => onSaveDraft({ type:"IV_INFUSION", data:d, lastStep:step, draftIdRef })} />
      )}
      <Steps steps={["IV Site Assessment","Infusion Details","Complications & Response","Signature"]} current={step} onGo={setStep} />

      {step===1 && <>
        <G3>
          <F label="Visit Date" req><input type="date" value={d.visitDate} onChange={e=>u("visitDate",e.target.value)} style={inp} /></F>
          <F label="IV Access Type" req>
            <select value={d.ivType} onChange={e=>u("ivType",e.target.value)} style={inp}>
              {["Peripheral","PICC","CVC (Central)","Port-A-Cath","Midline"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Blood Return Confirmed?">
            <ChipGroup options={["Yes","No","N/A"]} value={d.bloodReturn} onChange={v=>u("bloodReturn",v)} color={C.success} />
          </F>
        </G3>

        {d.ivType==="Peripheral" && <>
          <SH t="Peripheral Site Details" i="💉" color={C.info} />
          <G4>
            <F label="Site Location" req>
              <select value={d.siteLocation} onChange={e=>u("siteLocation",e.target.value)} style={inp}>
                <option value="">Select site...</option>
                {SITES_PERIPHERAL.map(s=><option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Gauge">
              <select value={d.gauge} onChange={e=>u("gauge",e.target.value)} style={inp}>
                <option value="">Select...</option>{GAUGES.map(g=><option key={g}>{g}</option>)}
              </select>
            </F>
            <F label="Insertion Date"><input type="date" value={d.insertionDate} onChange={e=>u("insertionDate",e.target.value)} style={inp} /></F>
            <F label="Inserted By"><input type="text" value={d.insertedBy} onChange={e=>u("insertedBy",e.target.value)} style={inp} /></F>
          </G4>
        </>}

        {["PICC","CVC (Central)","Port-A-Cath","Midline"].includes(d.ivType) && <>
          <SH t="Central Access Details" i="🔗" color={C.purple} />
          <G2>
            <F label="Catheter Type" req>
              <select value={d.cvcType} onChange={e=>u("cvcType",e.target.value)} style={inp}>
                <option value="">Select...</option>{CVC_TYPES.map(c=><option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Port / Lumen Used"><input type="text" value={d.cvcPort} onChange={e=>u("cvcPort",e.target.value)} style={inp} placeholder="e.g. Proximal, Distal, Single" /></F>
          </G2>
          <SH t="Flush Documentation" i="💧" color={C.info} />
          <Notice type="blue">
            <strong>CVC Flush Protocol:</strong> PICC → 20ml NS + 3ml Heparin 100u/ml q12h min.
            Hickman/Broviac → 3ml Heparin 100u/ml q24h min. Groshong → 20ml NS only q7 days.
            Port-A-Cath → 10ml NS + 5ml Heparin 100u/ml monthly min. Push/pause method required.
          </Notice>
          <G4>
            <F label="Pre-infusion Saline Flush"><ChipGroup options={["Yes","No"]} value={d.salineFlushPre} onChange={v=>u("salineFlushPre",v)} /></F>
            <F label="Post-infusion Saline Flush"><ChipGroup options={["Yes","No"]} value={d.salineFlushPost} onChange={v=>u("salineFlushPost",v)} /></F>
            <F label="Heparin Lock Flush"><ChipGroup options={["Yes","No","N/A"]} value={d.heparinFlushPost} onChange={v=>u("heparinFlushPost",v)} /></F>
            <F label="Flush Volume"><input type="text" value={d.flushVolume} onChange={e=>u("flushVolume",e.target.value)} style={inp} placeholder="e.g. 10ml NS" /></F>
          </G4>
        </>}

        <SH t="Site Assessment Scales" i="📊" color={C.warning} />
        <G2>
          <div>
            <p style={{ fontSize:12, fontWeight:700, color:C.g600, marginBottom:10, textTransform:"uppercase" }}>Phlebitis Scale (INS Standard)</p>
            {PHLEBITIS_SCALE.map(([score,desc])=>(
              <label key={score} onClick={()=>u("phlebitisScore",score)} style={{
                display:"flex", alignItems:"flex-start", gap:10, padding:"8px 12px",
                marginBottom:6, borderRadius:8, cursor:"pointer",
                background: d.phlebitisScore===score ? (score==="0"?"#F0FDF4":score==="1+"?"#FEF3C7":"#FEF2F2") : C.g100,
                border: `1.5px solid ${d.phlebitisScore===score?(score==="0"?C.success:score==="1+"?C.warning:C.error):C.g200}`,
                fontSize:13
              }}>
                <strong style={{ minWidth:24, color: score==="0"?C.success:score==="1+"?C.warning:C.error }}>{score}</strong>
                <span style={{ color:C.g800 }}>{desc}</span>
              </label>
            ))}
          </div>
          <div>
            <p style={{ fontSize:12, fontWeight:700, color:C.g600, marginBottom:10, textTransform:"uppercase" }}>Infiltration Scale (INS Standard)</p>
            {INFILTRATION_SCALE.map(([score,desc])=>(
              <label key={score} onClick={()=>u("infiltrationScore",score)} style={{
                display:"flex", alignItems:"flex-start", gap:10, padding:"8px 12px",
                marginBottom:6, borderRadius:8, cursor:"pointer",
                background: d.infiltrationScore===score ? (score==="0"?"#F0FDF4":score==="1"?"#FEF3C7":"#FEF2F2") : C.g100,
                border: `1.5px solid ${d.infiltrationScore===score?(score==="0"?C.success:score==="1"?C.warning:C.error):C.g200}`,
                fontSize:13
              }}>
                <strong style={{ minWidth:16, color: score==="0"?C.success:score==="1"?C.warning:C.error }}>{score}</strong>
                <span style={{ color:C.g800 }}>{desc}</span>
              </label>
            ))}
          </div>
        </G2>

        {(parseInt(d.phlebitisScore)||0)>=2 || parseInt(d.infiltrationScore)>=2 ? (
          <Notice type="red">
            <strong>🚨 Score ≥2: MD Notification Required.</strong> Per NNHC IV Therapy policy, phlebitis ≥2+ or infiltration ≥2
            requires immediate physician notification and documentation of actions taken. Discontinue IV if score ≥3.
          </Notice>
        ) : null}
        <BtnRow onNext={()=>setStep(2)} />
      </>}

      {step===2 && <>
        <SH t="Infusion Details" i="💧" />
        <G4>
          <F label="Start Time" req><input type="time" value={d.startTime} onChange={e=>u("startTime",e.target.value)} style={inp} /></F>
          <F label="Stop Time"><input type="time" value={d.stopTime} onChange={e=>u("stopTime",e.target.value)} style={inp} /></F>
          <F label="Rate (mL/hr)" req><input type="text" value={d.rate} onChange={e=>u("rate",e.target.value)} style={inp} /></F>
          <F label="Total Volume (mL)"><input type="text" value={d.totalVolume} onChange={e=>u("totalVolume",e.target.value)} style={inp} /></F>
        </G4>
        <G2>
          <F label="Primary Solution / Medication" req><input type="text" value={d.medication} onChange={e=>u("medication",e.target.value)} style={inp} placeholder="e.g. NS 0.9%, LR, D5W, Vancomycin..." /></F>
          <F label="Concentration"><input type="text" value={d.concentration} onChange={e=>u("concentration",e.target.value)} style={inp} placeholder="e.g. 1.25 g/250mL" /></F>
        </G2>

        <SH t="Medications Administered via IV" i="💊" />
        {d.meds.map((med,i)=>(
          <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 36px", gap:10, marginBottom:10, alignItems:"flex-end" }}>
            <F label={`Medication ${i+1}`}><input type="text" value={med.name} onChange={e=>{const m=[...d.meds];m[i]={...m[i],name:e.target.value};u("meds",m);}} style={inp} placeholder="Drug name" /></F>
            <F label="Dose"><input type="text" value={med.dose} onChange={e=>{const m=[...d.meds];m[i]={...m[i],dose:e.target.value};u("meds",m);}} style={inp} /></F>
            <F label="Time Given"><input type="time" value={med.time} onChange={e=>{const m=[...d.meds];m[i]={...m[i],time:e.target.value};u("meds",m);}} style={inp} /></F>
            <F label="Response"><input type="text" value={med.response} onChange={e=>{const m=[...d.meds];m[i]={...m[i],response:e.target.value};u("meds",m);}} style={inp} /></F>
            <button onClick={()=>u("meds",d.meds.filter((_,j)=>j!==i))} style={{ background:"none", border:`1.5px solid ${C.error}`, color:C.error, borderRadius:7, cursor:"pointer", padding:"9px 8px", marginBottom:0, fontSize:14 }}>×</button>
          </div>
        ))}
        <button onClick={()=>u("meds",[...d.meds,{name:"",dose:"",route:"IV",time:"",response:""}])} style={{ ...btnSec, fontSize:12, marginBottom:20 }}>+ Add Medication</button>

        <SH t="Equipment Maintenance" i="🔧" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>
          {[["tubingChanged","IV Tubing Changed?"],["dressChanged","Dressing Changed?"],["capChanged","Cap/PDD Changed?"]].map(([k,l])=>(
            <F key={k} label={l}><ChipGroup options={["Yes","No"]} value={d[k]} onChange={v=>u(k,v)} /></F>
          ))}
        </div>
        <BtnRow onBack={()=>setStep(1)} onNext={()=>setStep(3)} />
      </>}

      {step===3 && <>
        <SH t="Complications" i="⚠️" />
        <F label="Complications Noted (select all that apply)">
          <ChipGroup options={COMPLICATIONS} value={d.complications} onChange={v=>u("complications",v)} multi color={C.error} />
        </F>
        {(d.complications||[]).length > 0 && (
          <F label="Complication Notes">
            <textarea value={d.complicationNotes} onChange={e=>u("complicationNotes",e.target.value)}
              style={{ ...inp, minHeight:70, resize:"vertical" }} placeholder="Describe complications, interventions taken, patient response..." />
          </F>
        )}
        {(d.complications||[]).includes("Anaphylaxis signs") && (
          <Notice type="red">
            <strong>🚨 ANAPHYLAXIS PROTOCOL:</strong> Stop infusion immediately. Call 911. Administer epinephrine per
            emergency orders. Monitor vitals q5min. IV access must remain in place. Notify physician immediately.
            Document: time of reaction, symptoms, all interventions with exact timestamps.
          </Notice>
        )}

        <SH t="Patient Response" i="👤" />
        <G2>
          <F label="Patient Tolerance of Infusion">
            <select value={d.patientTolerance} onChange={e=>u("patientTolerance",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Tolerated well — no complaints","Mild discomfort — monitored","Moderate discomfort — interventions taken",
                "Infusion discontinued — complications","Patient refused infusion"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <div>
            <F label="MD/NP Notified?"><YNSelect value={d.mdNotified} onChange={v=>u("mdNotified",v)} /></F>
            {d.mdNotified==="YES" && <F label="Reason"><input type="text" value={d.mdReason} onChange={e=>u("mdReason",e.target.value)} style={inp} /></F>}
          </div>
        </G2>
        <F label="Clinical Narrative" mic="iv-narrative">
          <textarea id="iv-narrative" value={d.narrative} onChange={e=>u("narrative",e.target.value)}
            style={{ ...inp, minHeight:80, resize:"vertical" }}
            placeholder="Document infusion progress, patient education, vital sign trends, nursing interventions, and follow-up plan..." />
        </F>

        <PhotoUpload
          docId={null}
          formType="IV_INFUSION"
          onUploaded={(paths) =>
            setD(prev => ({ ...prev, photoPaths: paths }))}
        />

        <SH t="RN Signature" i="✍️" />
        <Notice type="gold">
          <strong>NC BON Requirement:</strong> IV infusion record requires RN signature only. LPN may not sign
          IV infusion documentation as the administering clinician. Per NNHC IV Therapy Policy, all infusion
          records are retained in the clinical record for a minimum of 7 years.
        </Notice>
        <SigPad ref={rnSigPadRef} label="REGISTERED NURSE (RN) SIGNATURE — IV INFUSION RECORD" signed={rnSig} onSigned={setRnSig} />
        <BtnRow onBack={()=>setStep(2)} onNext={()=>{
            const rn = sigFromPad(rnSig, rnSigPadRef, "REGISTERED NURSE (RN) SIGNATURE — IV INFUSION RECORD");
            if (!rn) return;
            onComplete({type:"IV_INFUSION",data:d,rnSig:rn,patient,draftIdRef});
          }}
          nextLabel="✓ Submit IV Infusion Record" nextStyle={btnGold} disabled={!rnSig} />
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM 4: VENT / TRACH CARE NOTE
// Based on: NNHC OXYGEN policies (TRACHEOSTOMY CARE, TRACHEOTOMY SUCTIONING,
// MECHANICAL VENTILATION, VENT DEPENDENT PATIENT ASSESSMENT)
// ─────────────────────────────────────────────────────────────────────────────
export function VentTrachForm({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke = rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"VENT_TRACH", data:d, lastStep:step, draftIdRef });
  };
  const [d, setD] = useState(() => ({
    visitDate: new Date().toISOString().split("T")[0],
    visitTime: new Date().toTimeString().slice(0,5),
    visitType: "Routine",
    mechVent: "Yes",
    // Trach
    trachType: "", trachSize: "", trachBrand: "", trachInsertionDate: "", trachLastChanged: "",
    cuffedTrach: "No", cuffPressure: "",
    innercannula: "Yes",
    // Vent settings
    ventMode: "", ventRate: "", fiO2: "", tidalVolume: "", peep: "", pip: "", ieRatio: "",
    sensitivity: "", alarmHighPressure: "", alarmLowPressure: "", alarmLowO2: "",
    // Circuit
    circuitInspected: "Yes", circuitLeaks: "No", humidifierFunctioning: "Yes",
    circuitWaterLevel: "", lastCircuitChange: "",
    // Respiratory assessment
    respRate: "", o2sat: "", heartRate: "", bp: "", temp: "", skinColor: "",
    breathSoundsRUL: "", breathSoundsRLL: "", breathSoundsLUL: "", breathSoundsLLL: "",
    breathSoundsAdventitious: [],
    chestRise: "Symmetrical", workOfBreathing: "",
    // Trach care
    trachCarePerformed: "Yes", suctionPerformed: "Yes",
    suctionFrequency: "", suctionPressure: "",
    secretionColor: "", secretionConsistency: "", secretionOdor: "", secretionAmount: "",
    oxygenPreSuction: "Yes", oxygenPostSuction: "Yes",
    innerCannulaCleaned: "Yes", innerCannilaMethod: "Hydrogen peroxide then NS rinse",
    stomaSiteCondition: "", stomaDressing: "Changed", stomaCleaned: "Yes",
    tracheostomyTiesCondition: "", tracheostomyTiesChanged: "No",
    // Alarms during visit
    alarmsActivated: "No", alarmTypes: [], alarmInterventions: "",
    // Vent checks
    ventChecksPassed: "Yes", ventConcerns: "",
    // Nutrition/hydration
    enteralFeedingRunning: "No", enteralFeedRate: "", enteralFormula: "",
    // Red flags
    redFlagChecklist: {},
    narrative: "",
    mdNotified: "No", mdTime: "", mdOrders: "",
    ...(initialDraft?.form_data || {}),
  }));
  const u = (k,v) => setD(p=>({...p,[k]:v}));

  const VENT_MODES = ["AC/VC (Assist Control Volume Control)","AC/PC (Assist Control Pressure Control)",
    "SIMV-VC","SIMV-PC","PSV (Pressure Support)","CPAP","BiPAP","PRVC (Pressure Regulated Volume Control)","Other"];
  const BREATH_SOUNDS_ADV = ["Crackles (bilateral)","Crackles (left)","Crackles (right)","Rhonchi",
    "Wheezing","Stridor","Pleural friction rub","Absent (left)","Absent (right)","Diminished"];
  const ALARM_TYPES = ["High pressure","Low pressure","Low O2","Low minute volume","Apnea",
    "Power/electrical","Exhalation valve","Circuit disconnect"];
  const RED_FLAGS = [
    ["secretionChange","Change in secretion color, consistency, or odor"],
    ["bleeding","Bleeding from tracheostomy"],
    ["fever","Fever (temp above/below established range)"],
    ["tachypnea","Tachypnea — unresolved with interventions"],
    ["asymChest","Asymmetrical chest wall movement"],
    ["highPIP","PIP above ordered alarm setting"],
    ["lowPEEP","Unable to maintain PEEP"],
    ["obstruction","Unresolved airway obstruction post-suction"],
    ["cyanosis","Cyanosis or skin color change"],
    ["agitation","Sudden agitation or restlessness"],
  ];

  const redFlagCount = RED_FLAGS.filter(([k])=>d.redFlagChecklist[k]==="YES").length;

  return (
    <div style={{ fontFamily:"DM Sans, sans-serif" }}>
      <Notice type="gold">
        <strong>NNHC Vent/Trach Policy:</strong> Complete system assessment required before every shift and PRN.
        Every shift: document vent settings verification, circuit check, trach care, suctioning (if performed),
        breath sounds in all lobes, and red flag screening. Per NNHC Vent Dependent Patient Assessment policy:
        notify nursing supervisor of ANY physician contact, new orders, or unusual occurrences.
      </Notice>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 4 ? handleSaveDraftClick : () => onSaveDraft({ type:"VENT_TRACH", data:d, lastStep:step, draftIdRef })} />
      )}
      <Steps steps={["Trach & Vent Settings","Respiratory Assessment","Trach Care & Suctioning","Red Flags & Signatures"]} current={step} onGo={setStep} />

      {step===1 && <>
        <G3>
          <F label="Visit Date" req><input type="date" value={d.visitDate} onChange={e=>u("visitDate",e.target.value)} style={inp} /></F>
          <F label="Visit Time" req><input type="time" value={d.visitTime} onChange={e=>u("visitTime",e.target.value)} style={inp} /></F>
          <F label="Visit Type">
            <select value={d.visitType} onChange={e=>u("visitType",e.target.value)} style={inp}>
              {["Routine","Start of Shift","End of Shift","PRN — Patient Change","Emergency Response"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>

        <SH t="Tracheostomy Details" i="🫁" />
        <G4>
          <F label="Trach Type" req>
            <select value={d.trachType} onChange={e=>u("trachType",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Cuffed — standard","Cuffed — fenestrated","Uncuffed — standard","Uncuffed — fenestrated",
                "Metal (Jackson)","PMV (Passy Muir Valve) in use","Pediatric cuffless","Other"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Size (mm ID)"><input type="text" value={d.trachSize} onChange={e=>u("trachSize",e.target.value)} style={inp} placeholder="e.g. 8.0mm" /></F>
          <F label="Brand"><input type="text" value={d.trachBrand} onChange={e=>u("trachBrand",e.target.value)} style={inp} placeholder="e.g. Bivona, Shiley" /></F>
          <F label="Date Last Changed"><input type="date" value={d.trachLastChanged} onChange={e=>u("trachLastChanged",e.target.value)} style={inp} /></F>
        </G4>
        <G2>
          <div>
            <F label="Cuffed Trach?"><ChipGroup options={["Yes","No"]} value={d.cuffedTrach} onChange={v=>u("cuffedTrach",v)} /></F>
            {d.cuffedTrach==="Yes" && <F label="Cuff Pressure (mmHg)"><input type="text" value={d.cuffPressure} onChange={e=>u("cuffPressure",e.target.value)} style={inp} placeholder="Target: 20–25 mmHg" /></F>}
          </div>
          <F label="Inner Cannula Present?"><ChipGroup options={["Yes","No"]} value={d.innercannula} onChange={v=>u("innercannula",v)} /></F>
        </G2>

        <F label="Patient on Mechanical Ventilation?">
          <ChipGroup options={["Yes","No — Trach only (spontaneous breathing)","No — Trach with O2"]} value={d.mechVent} onChange={v=>u("mechVent",v)} />
        </F>

        {d.mechVent==="Yes" && <>
          <SH t="Ventilator Settings (Verify Against Physician Orders)" i="⚙️" />
          <G4>
            <F label="Vent Mode" req>
              <select value={d.ventMode} onChange={e=>u("ventMode",e.target.value)} style={inp}>
                <option value="">Select mode...</option>
                {VENT_MODES.map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
            <F label="Set Rate (breaths/min)"><input type="text" value={d.ventRate} onChange={e=>u("ventRate",e.target.value)} style={inp} /></F>
            <F label="FiO2 (%)"><input type="text" value={d.fiO2} onChange={e=>u("fiO2",e.target.value)} style={inp} placeholder="21–100" /></F>
            <F label="Tidal Volume (mL)"><input type="text" value={d.tidalVolume} onChange={e=>u("tidalVolume",e.target.value)} style={inp} /></F>
          </G4>
          <G4>
            <F label="PEEP (cmH₂O)"><input type="text" value={d.peep} onChange={e=>u("peep",e.target.value)} style={inp} /></F>
            <F label="PIP (cmH₂O)"><input type="text" value={d.pip} onChange={e=>u("pip",e.target.value)} style={inp} /></F>
            <F label="I:E Ratio"><input type="text" value={d.ieRatio} onChange={e=>u("ieRatio",e.target.value)} style={inp} placeholder="e.g. 1:2" /></F>
            <F label="Sensitivity"><input type="text" value={d.sensitivity} onChange={e=>u("sensitivity",e.target.value)} style={inp} /></F>
          </G4>
          <SH t="Alarm Settings (Verify Against Orders)" i="🔔" />
          <G3>
            <F label="High Pressure Alarm (cmH₂O)"><input type="text" value={d.alarmHighPressure} onChange={e=>u("alarmHighPressure",e.target.value)} style={inp} /></F>
            <F label="Low Pressure Alarm (cmH₂O)"><input type="text" value={d.alarmLowPressure} onChange={e=>u("alarmLowPressure",e.target.value)} style={inp} /></F>
            <F label="Low O2 Alarm (%)"><input type="text" value={d.alarmLowO2} onChange={e=>u("alarmLowO2",e.target.value)} style={inp} /></F>
          </G3>
          <SH t="Circuit Inspection" i="🔍" />
          <G3>
            <F label="Circuit Inspected?"><ChipGroup options={["Yes","No"]} value={d.circuitInspected} onChange={v=>u("circuitInspected",v)} color={C.success} /></F>
            <F label="Leaks/Disconnections?"><ChipGroup options={["Yes","No"]} value={d.circuitLeaks} onChange={v=>u("circuitLeaks",v)} color={d.circuitLeaks==="Yes"?C.error:C.success} /></F>
            <F label="Humidifier Functioning?"><ChipGroup options={["Yes","No","N/A"]} value={d.humidifierFunctioning} onChange={v=>u("humidifierFunctioning",v)} color={C.success} /></F>
          </G3>
        </>}
        <BtnRow onNext={()=>setStep(2)} />
      </>}

      {step===2 && <>
        <SH t="Respiratory Assessment" i="🩺" />
        <G4>
          <F label="Resp Rate (client)" req><input type="text" value={d.respRate} onChange={e=>u("respRate",e.target.value)} style={inp} /></F>
          <F label="O2 Saturation (%)" req><input type="text" value={d.o2sat} onChange={e=>u("o2sat",e.target.value)} style={inp} /></F>
          <F label="Heart Rate"><input type="text" value={d.heartRate} onChange={e=>u("heartRate",e.target.value)} style={inp} /></F>
          <F label="B/P"><input type="text" value={d.bp} onChange={e=>u("bp",e.target.value)} style={inp} placeholder="___/___" /></F>
        </G4>
        <G3>
          <F label="Temperature"><input type="text" value={d.temp} onChange={e=>u("temp",e.target.value)} style={inp} /></F>
          <F label="Chest Rise"><ChipGroup options={["Symmetrical","Asymmetrical — Left reduced","Asymmetrical — Right reduced"]} value={d.chestRise} onChange={v=>u("chestRise",v)} /></F>
          <F label="Skin Color / Perfusion">
            <select value={d.skinColor} onChange={e=>u("skinColor",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Pink/warm — normal","Pale","Cyanotic","Mottled","Gray","Flushed","Jaundiced"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>

        <SH t="Breath Sounds — All Lobes Required" i="🫁" />
        <Notice type="blue">
          Per NNHC Vent Dependent Patient Assessment policy: assess breath sounds with stethoscope in ALL lobes every shift.
          Document adventitious sounds (crackles, rhonchi, wheezing) with location and timing.
        </Notice>
        <G4>
          {[["breathSoundsRUL","Right Upper Lobe"],["breathSoundsLUL","Left Upper Lobe"],
            ["breathSoundsRLL","Right Lower Lobe"],["breathSoundsLLL","Left Lower Lobe"]].map(([k,l])=>(
            <F key={k} label={l} req>
              <select value={d[k]} onChange={e=>u(k,e.target.value)} style={inp}>
                <option value="">Select...</option>
                {["Clear","Diminished","Absent","Coarse crackles","Fine crackles","Rhonchi","Wheezing","Stridor"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
          ))}
        </G4>
        <F label="Adventitious Sounds (additional — select all)">
          <ChipGroup options={BREATH_SOUNDS_ADV} value={d.breathSoundsAdventitious} onChange={v=>u("breathSoundsAdventitious",v)} multi color={C.warning} />
        </F>
        <F label="Work of Breathing / Accessory Muscle Use">
          <select value={d.workOfBreathing} onChange={e=>u("workOfBreathing",e.target.value)} style={inp}>
            <option value="">Select...</option>
            {["Unlabored — on vent support","Mild increased work — retractions mild","Moderate increased work",
              "Severe distress — retractions marked","Paradoxical breathing","Apneic episodes noted"].map(v=><option key={v}>{v}</option>)}
          </select>
        </F>
        <BtnRow onBack={()=>setStep(1)} onNext={()=>setStep(3)} />
      </>}

      {step===3 && <>
        <SH t="Tracheostomy Care" i="🔬" />
        <G3>
          <F label="Trach Care Performed?"><ChipGroup options={["Yes","No — Not indicated"]} value={d.trachCarePerformed} onChange={v=>u("trachCarePerformed",v)} color={C.success} /></F>
          <F label="Stoma Cleaned?"><ChipGroup options={["Yes","No"]} value={d.stomaCleaned} onChange={v=>u("stomaCleaned",v)} color={C.success} /></F>
          <F label="Stoma Dressing Status">
            <select value={d.stomaDressing} onChange={e=>u("stomaDressing",e.target.value)} style={inp}>
              {["Changed","Intact — not changed","Not applicable"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>
        <G2>
          <F label="Stoma Site Condition">
            <select value={d.stomaSiteCondition} onChange={e=>u("stomaSiteCondition",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Clean, dry, intact — no signs of infection","Mild erythema — monitored","Granulation tissue present",
                "Purulent drainage — provider notified","Bleeding — provider notified",
                "Skin breakdown around stoma","Stoma size change — provider notified"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <G2>
            <F label="Trach Ties Condition">
              <select value={d.tracheostomyTiesCondition} onChange={e=>u("tracheostomyTiesCondition",e.target.value)} style={inp}>
                <option value="">Select...</option>
                {["Intact — secure (1 finger space)","Soiled — changed","Loose — tightened","New ties applied"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
            <F label="Ties Changed?"><ChipGroup options={["Yes","No"]} value={d.tracheostomyTiesChanged} onChange={v=>u("tracheostomyTiesChanged",v)} /></F>
          </G2>
        </G2>
        {d.innercannula==="Yes" && <G2>
          <F label="Inner Cannula Cleaned?"><ChipGroup options={["Yes","No","Disposable — replaced"]} value={d.innerCannulaCleaned} onChange={v=>u("innerCannulaCleaned",v)} color={C.success} /></F>
          <F label="Cleaning Method">
            <select value={d.innerCannilaMethod} onChange={e=>u("innerCannilaMethod",e.target.value)} style={inp}>
              {["Hydrogen peroxide then NS rinse","NS rinse only","Warm soapy water then rinse",
                "Disposable replaced","Sent for reprocessing"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G2>}

        <SH t="Tracheal Suctioning" i="🌀" />
        <F label="Suctioning Performed?"><ChipGroup options={["Yes","No — Not indicated","No — Patient refused"]} value={d.suctionPerformed} onChange={v=>u("suctionPerformed",v)} /></F>
        {d.suctionPerformed==="Yes" && <>
          <G4>
            <F label="Frequency (passes)"><input type="text" value={d.suctionFrequency} onChange={e=>u("suctionFrequency",e.target.value)} style={inp} placeholder="e.g. 3 passes" /></F>
            <F label="Suction Pressure (mmHg)"><input type="text" value={d.suctionPressure} onChange={e=>u("suctionPressure",e.target.value)} style={inp} placeholder="e.g. 80–120 mmHg" /></F>
            <F label="O2 Pre-suction?"><ChipGroup options={["Yes","No"]} value={d.oxygenPreSuction} onChange={v=>u("oxygenPreSuction",v)} color={C.success} /></F>
            <F label="O2 Post-suction?"><ChipGroup options={["Yes","No"]} value={d.oxygenPostSuction} onChange={v=>u("oxygenPostSuction",v)} color={C.success} /></F>
          </G4>
          <G4>
            <F label="Secretion Color">
              <select value={d.secretionColor} onChange={e=>u("secretionColor",e.target.value)} style={inp}>
                <option value="">Select...</option>
                {["Clear","White/frothy","Yellow","Green","Tan/brown","Blood-tinged","Frank blood"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
            <F label="Consistency">
              <select value={d.secretionConsistency} onChange={e=>u("secretionConsistency",e.target.value)} style={inp}>
                <option value="">Select...</option>
                {["Thin/watery","Moderately thick","Thick/tenacious","Mucoid plug"].map(v=><option key={v}>{v}</option>)}
              </select>
            </F>
            <F label="Odor"><ChipGroup options={["None","Mild","Foul"]} value={d.secretionOdor} onChange={v=>u("secretionOdor",v)} color={d.secretionOdor==="None"?C.success:C.error} /></F>
            <F label="Amount"><ChipGroup options={["Small","Moderate","Large"]} value={d.secretionAmount} onChange={v=>u("secretionAmount",v)} /></F>
          </G4>
        </>}

        {(d.secretionColor==="Frank blood" || d.stomaSiteCondition?.includes("Bleeding")) && (
          <Notice type="red">
            <strong>🚨 BLEEDING FROM TRACHEOSTOMY — RED FLAG:</strong> Per NNHC Vent Dependent Patient Assessment policy,
            recurrent or copious bleeding from trachea requires immediate physician notification.
            Document time, amount, and all interventions.
          </Notice>
        )}

        <SH t="Alarm Events During Visit" i="🔔" />
        <F label="Alarms Activated During Visit?">
          <ChipGroup options={["Yes","No"]} value={d.alarmsActivated} onChange={v=>u("alarmsActivated",v)} />
        </F>
        {d.alarmsActivated==="Yes" && <>
          <F label="Alarm Types (select all)">
            <ChipGroup options={ALARM_TYPES} value={d.alarmTypes} onChange={v=>u("alarmTypes",v)} multi color={C.error} />
          </F>
          <F label="Interventions Taken">
            <textarea value={d.alarmInterventions} onChange={e=>u("alarmInterventions",e.target.value)} style={{ ...inp, minHeight:70 }}
              placeholder="Describe alarm cause, interventions taken, and patient response. Reference alarm response chart in NNHC policy." />
          </F>
        </>}
        <BtnRow onBack={()=>setStep(2)} onNext={()=>setStep(4)} />
      </>}

      {step===4 && <>
        <SH t="Red Flag Screening" i="🚨" />
        <Notice type={redFlagCount>0?"red":"green"}>
          {redFlagCount>0
            ? <strong>⚠️ {redFlagCount} RED FLAG(S) IDENTIFIED — Physician notification required. Document time, orders received, and all interventions.</strong>
            : <strong>✓ No red flags identified this visit.</strong>}
        </Notice>
        <div style={{ border:`1.5px solid ${C.g200}`, borderRadius:10, overflow:"hidden", marginBottom:20 }}>
          <div style={{ display:"grid", gridTemplateColumns:"3fr 80px 80px 80px", background:C.purple, color:C.white,
            padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase" }}>
            <span>Red Flag Criterion</span><span style={{textAlign:"center"}}>YES</span>
            <span style={{textAlign:"center"}}>NO</span><span style={{textAlign:"center"}}>N/A</span>
          </div>
          {RED_FLAGS.map(([k,l])=>(
            <div key={k} style={{ display:"grid", gridTemplateColumns:"3fr 80px 80px 80px",
              padding:"9px 12px", borderBottom:`1px solid ${C.g100}`,
              background: d.redFlagChecklist[k]==="YES" ? "#FEF2F2" : C.white }}>
              <span style={{ fontSize:13, color:C.g800 }}>{l}</span>
              {["YES","NO","N/A"].map(opt=>(
                <div key={opt} onClick={()=>setD(p=>({...p,redFlagChecklist:{...p.redFlagChecklist,[k]:opt}}))} style={{
                  textAlign:"center", padding:"4px 8px", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700,
                  background: d.redFlagChecklist[k]===opt ? (opt==="YES"?"#FEF2F2":opt==="NO"?"#F0FDF4":C.g100) : C.white,
                  color: d.redFlagChecklist[k]===opt ? (opt==="YES"?C.error:opt==="NO"?C.success:C.g400) : C.g400,
                  border: `1.5px solid ${d.redFlagChecklist[k]===opt?(opt==="YES"?C.error:opt==="NO"?C.success:C.g200):C.g200}`,
                  margin:"0 4px", transition:"all 0.15s"
                }}>{opt}</div>
              ))}
            </div>
          ))}
        </div>

        <G2>
          <div>
            <F label="MD/Nursing Supervisor Notified?"><YNSelect value={d.mdNotified} onChange={v=>u("mdNotified",v)} /></F>
            {d.mdNotified==="YES" && <G2><F label="Time"><input type="time" value={d.mdTime} onChange={e=>u("mdTime",e.target.value)} style={inp} /></F><F label="Orders Given"><input type="text" value={d.mdOrders} onChange={e=>u("mdOrders",e.target.value)} style={inp} /></F></G2>}
          </div>
          <F label="Clinical Narrative" mic="vent-narrative">
            <textarea id="vent-narrative" value={d.narrative} onChange={e=>u("narrative",e.target.value)} style={{ ...inp, minHeight:100, resize:"vertical" }}
              placeholder="Document overall patient status, tolerance of care, caregiver education, changes from baseline, and follow-up plan..." />
          </F>
        </G2>

        <PhotoUpload
          docId={null}
          formType="VENT_TRACH"
          onUploaded={(paths) =>
            setD(prev => ({ ...prev, photoPaths: paths }))}
        />

        <SH t="Signatures" i="✍️" />
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <SigPad ref={rnSigPadRef} label="RN / LPN CLINICIAN SIGNATURE — VENT/TRACH CARE NOTE" signed={rnSig} onSigned={setRnSig} />
          <SigPad ref={clientSigPadRef} label="CAREGIVER / FAMILY ACKNOWLEDGMENT (OPTIONAL)" signed={clientSig} onSigned={setClientSig} />
        </div>
        <BtnRow onBack={()=>setStep(3)} onNext={()=>{
            const rn = sigFromPad(rnSig, rnSigPadRef, "RN / LPN CLINICIAN SIGNATURE — VENT/TRACH CARE NOTE");
            const client = sigFromPad(clientSig, clientSigPadRef, "CAREGIVER / FAMILY ACKNOWLEDGMENT");
            if (!rn) return;
            onComplete({type:"VENT_TRACH",data:d,rnSig:rn,clientSig:client,patient,draftIdRef});
          }}
          nextLabel="✓ Submit Vent/Trach Care Note" nextStyle={btnGold} disabled={!rnSig} />
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM 5: SKILLED NURSING ASSESSMENT (Initial / Reassessment)
// Based on: NNHC Assessment forms + ACHC HC2-1A + NC 10A NCAC 13J
// ─────────────────────────────────────────────────────────────────────────────
// SkillNursingAssessment step map (1-indexed):
//   1 — Vitals & History         (assessment date, type, vitals, pain, meds, ICD-10)
//   2 — Systems Review           (neuro / cardio / resp / GI / GU / integument)
//   3 — Functional & Safety      (ADLs, equipment, home safety, caregiver)
//   4 — Goals                    (short/long-term goals, plan changes, narrative)
//   5 — Documents                (DocumentUpload — supporting files / voice memos)
//   6 — Signature                (RN + patient/client; submit)
export function SkillNursingAssessment({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke = rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"SKILLED_NURSING", data:d, lastStep:step, draftIdRef });
  };
  const [d, setD] = useState(() => ({
    assessDate: new Date().toISOString().split("T")[0],
    assessType: "Initial",
    assessTime: new Date().toTimeString().slice(0,5),
    // Demographics
    primaryDx: null, secondaryDx: [], allergies: "", codeStatus: "Full Code",
    primaryLanguage: "English", interpreterNeeded: "No",
    // Vitals
    bp: "", pulse: "", resp: "", temp: "", o2sat: "", height: "", weight: "", bmi: "",
    painScale: "0", painLocation: "", painQuality: "",
    // Systems
    neuro: { consciousness:"Alert", orientation:[], speech:"", pupils:"", cognition:"" },
    cardio: { rhythm:"Regular", pulses:"", capRefill:"", edema:"", jvd:"No", mucosalColor:"" },
    respiratorySystem: { effort:"Unlabored", sounds:{}, pattern:"", o2Delivery:"", cough:"" },
    gi: { abdomen:"Soft, non-tender", bowelSounds:"Present all quadrants", lastBM:"", tube:"No", ostomy:"No" },
    gu: { bladder:"Continent", catheter:"No", urineColor:"", edema:"" },
    musculo: { strength:"", rom:"", gait:"", fallRisk:"", assistDevice:"" },
    integument: { condition:"Intact", wounds:"No", pressure:"No", turgor:"", moisture:"" },
    // Functional
    adls: {},
    functional: { ambulation:"", transfers:"", equipment:[] },
    // Medications
    medications: [{ name:"", dose:"", route:"", frequency:"", prescriber:"" }],
    highAlertMeds: [],
    medicationReconciled: "Yes",
    // Safety
    safetyAssessment: {}, homeSafety: [], equipmentInHome: [],
    // Caregiver
    primaryCaregiver: "", caregiverRelationship: "", caregiverCapacity: "", caregiverTraining: [],
    // Goals / POC
    shortTermGoals: "", longTermGoals: "", dischargeGoals: "",
    pocChangesNeeded: "No", pocNotes: "",
    followUpNeeded: "", mdNotified: "No",
    narrative: "",
    ...(initialDraft?.form_data || {}),
  }));
  const u = (k,v) => setD(p=>({...p,[k]:v}));
  const un = (section,k,v) => setD(p=>({...p,[section]:{...p[section],[k]:v}}));

  const ADLS = ["Bathing","Dressing","Grooming","Oral care","Toileting","Feeding","Meal prep","Medication management","Ambulation","Transfers","Laundry","Housekeeping"];
  const ADL_STATUS = ["Independent","Supervision","Min Assist (25%)","Mod Assist (50%)","Max Assist (75%)","Total Assist (100%)","N/A"];
  const EQUIPMENT = ["Hospital bed","Side rails","Trapeze","Wheelchair","Walker","Cane","Crutches","Hoyer lift",
    "Commode","Bath bench","Grab bars","O2 concentrator","Portable O2","Nebulizer","Suction machine",
    "Feeding pump","IV pump","Vent","CPM machine","TENS unit","CPAP/BiPAP","Other"];
  const HOME_SAFETY = ["Adequate lighting","Clutter-free pathways","Throw rugs removed","Grab bars in bathroom",
    "Non-slip mats","Smoke detector working","CO detector working","Emergency contacts posted",
    "Medications stored safely","Sharp objects stored safely","Temperature control adequate","Pets secured during visits"];
  const HIGH_ALERT = ["Insulin","Anticoagulants (warfarin/heparin/Xarelto)","Opioids/narcotics","Benzodiazepines",
    "Digoxin","Lithium","Methotrexate","Chemotherapy agents","Seizure medications"];
  const NEURO_ORIENT = ["Person","Place","Time","Situation/Event"];

  return (
    <div style={{ fontFamily:"DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 6 ? handleSaveDraftClick : () => onSaveDraft({ type:"SKILLED_NURSING", data:d, lastStep:step, draftIdRef })} />
      )}
      <Steps steps={["Vitals & History","Systems Review","Functional & Safety","Goals","Documents","Signature"]} current={step} onGo={setStep} />

      {step===1 && <>
        <G4>
          <F label="Assessment Date" req><input type="date" value={d.assessDate} onChange={e=>u("assessDate",e.target.value)} style={inp} /></F>
          <F label="Assessment Time"><input type="time" value={d.assessTime} onChange={e=>u("assessTime",e.target.value)} style={inp} /></F>
          <F label="Assessment Type" req>
            <select value={d.assessType} onChange={e=>u("assessType",e.target.value)} style={inp}>
              {["Initial","Reassessment","Annual Evaluation","Interim — Condition Change","Discharge"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Code Status">
            <select value={d.codeStatus} onChange={e=>u("codeStatus",e.target.value)} style={inp}>
              {["Full Code","DNR","DNI","DNR/DNI","Limited Intervention","Comfort/Hospice"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G4>
        <F label="Primary Diagnosis (ICD-10)" req>
          <Icd10Search singleSelect
            value={d.primaryDx}
            onChange={(v) => u("primaryDx", v)}
            placeholder="Search primary ICD-10 code or description..." />
        </F>
        <F label="Secondary Diagnoses">
          <Icd10Search
            value={d.secondaryDx || []}
            onChange={(v) => u("secondaryDx", v)}
            placeholder="Add additional ICD-10 codes..." />
        </F>
        <F label="Known Allergies" req>
          <input type="text" value={d.allergies}
            onChange={e=>u("allergies",e.target.value)} style={inp}
            placeholder="NKDA or list" />
        </F>
        <G2>
          <F label="Primary Language">
            <select value={d.primaryLanguage} onChange={e=>u("primaryLanguage",e.target.value)} style={inp}>
              {["English","Spanish","Vietnamese","Haitian Creole","French","Mandarin","Korean","Arabic","Sign Language","Other"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Interpreter / ADA Assistance Needed?"><ChipGroup options={["No","Yes — Language","Yes — ADA/Device"]} value={d.interpreterNeeded} onChange={v=>u("interpreterNeeded",v)} /></F>
        </G2>

        <SH t="Vital Signs" i="🩺" />
        <G4>
          <F label="B/P" req><input type="text" value={d.bp} onChange={e=>u("bp",e.target.value)} style={inp} placeholder="___/___" /></F>
          <F label="Pulse" req><input type="text" value={d.pulse} onChange={e=>u("pulse",e.target.value)} style={inp} placeholder="bpm" /></F>
          <F label="Resp Rate" req><input type="text" value={d.resp} onChange={e=>u("resp",e.target.value)} style={inp} placeholder="breaths/min" /></F>
          <F label="Temperature" req><input type="text" value={d.temp} onChange={e=>u("temp",e.target.value)} style={inp} placeholder="°F" /></F>
        </G4>
        <G4>
          <F label="O2 Saturation (%)" req><input type="text" value={d.o2sat} onChange={e=>u("o2sat",e.target.value)} style={inp} /></F>
          <F label="Height"><input type="text" value={d.height} onChange={e=>u("height",e.target.value)} style={inp} placeholder="ft/in or cm" /></F>
          <F label="Weight"><input type="text" value={d.weight} onChange={e=>u("weight",e.target.value)} style={inp} placeholder="lbs or kg" /></F>
          <F label="BMI (calc)">
            <input type="text" value={d.bmi} onChange={e=>u("bmi",e.target.value)} style={{ ...inp, background:C.g100 }} placeholder="Auto or enter" />
          </F>
        </G4>

        <SH t="Pain Assessment" i="😣" />
        <G3>
          <F label="Pain Scale (0–10)" req>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
              {[...Array(11)].map((_,i)=>(
                <div key={i} onClick={()=>u("painScale",String(i))} style={{
                  width:32, height:32, borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer", fontWeight:700, fontSize:12,
                  background: d.painScale===String(i)?(i<=3?C.success:i<=6?C.warning:C.error):C.g100,
                  color: d.painScale===String(i)?C.white:C.g600,
                  border:`1.5px solid ${d.painScale===String(i)?(i<=3?C.success:i<=6?C.warning:C.error):C.g200}`
                }}>{i}</div>
              ))}
            </div>
          </F>
          <F label="Pain Location"><input type="text" value={d.painLocation} onChange={e=>u("painLocation",e.target.value)} style={inp} /></F>
          <F label="Pain Quality (patient words)"><input type="text" value={d.painQuality} onChange={e=>u("painQuality",e.target.value)} style={inp} placeholder="e.g. burning, aching..." /></F>
        </G3>

        <SH t="Medication Review" i="💊" />
        {d.medications.map((med,i)=>(
          <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 36px", gap:10, marginBottom:10, alignItems:"flex-end" }}>
            <F label={`Medication ${i+1}`}><input type="text" value={med.name} onChange={e=>{const m=[...d.medications];m[i]={...m[i],name:e.target.value};u("medications",m);}} style={inp} placeholder="Drug name" /></F>
            <F label="Dose"><input type="text" value={med.dose} onChange={e=>{const m=[...d.medications];m[i]={...m[i],dose:e.target.value};u("medications",m);}} style={inp} /></F>
            <F label="Route"><input type="text" value={med.route} onChange={e=>{const m=[...d.medications];m[i]={...m[i],route:e.target.value};u("medications",m);}} style={inp} placeholder="PO/IV/SQ..." /></F>
            <F label="Frequency"><input type="text" value={med.frequency} onChange={e=>{const m=[...d.medications];m[i]={...m[i],frequency:e.target.value};u("medications",m);}} style={inp} placeholder="BID, TID..." /></F>
            <F label="Prescriber"><input type="text" value={med.prescriber} onChange={e=>{const m=[...d.medications];m[i]={...m[i],prescriber:e.target.value};u("medications",m);}} style={inp} /></F>
            <button onClick={()=>u("medications",d.medications.filter((_,j)=>j!==i))} style={{ background:"none", border:`1.5px solid ${C.error}`, color:C.error, borderRadius:7, cursor:"pointer", padding:"9px 8px", marginBottom:0, fontSize:14 }}>×</button>
          </div>
        ))}
        <button onClick={()=>u("medications",[...d.medications,{name:"",dose:"",route:"",frequency:"",prescriber:""}])} style={{ ...btnSec, fontSize:12, marginBottom:8 }}>+ Add Medication</button>
        <F label="High-Alert Medications Present (select all that apply)">
          <ChipGroup options={HIGH_ALERT} value={d.highAlertMeds} onChange={v=>u("highAlertMeds",v)} multi color={C.error} />
        </F>
        <BtnRow onNext={()=>setStep(step + 1)} />
      </>}

      {step===2 && <>
        <SH t="Neurological" i="🧠" />
        <G3>
          <F label="Level of Consciousness">
            <ChipGroup options={["Alert","Drowsy","Lethargic","Obtunded","Stuporous","Comatose"]}
              value={d.neuro.consciousness} onChange={v=>un("neuro","consciousness",v)} />
          </F>
          <F label="Oriented To (select all)">
            <ChipGroup options={NEURO_ORIENT} value={d.neuro.orientation} onChange={v=>un("neuro","orientation",v)} multi />
          </F>
          <F label="Cognition / Memory">
            <select value={d.neuro.cognition} onChange={e=>un("neuro","cognition",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Intact — baseline","Mild impairment — forgetful","Moderate impairment","Severe impairment",
                "Dementia — known baseline","Delirium — new onset","Hallucinations noted"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>

        <SH t="Cardiovascular" i="❤️" />
        <G4>
          {[["rhythm","Heart Rhythm",["Regular","Irregular","Irregular-irregular"]],
            ["capRefill","Cap Refill",["<2 sec — normal",">2 sec — delayed","Not assessed"]],
            ["edema","Peripheral Edema",["None","+1 Trace","+2 Mild","+3 Moderate","+4 Severe"]],
            ["jvd","JVD Present?",["No","Yes","Unable to assess"]]].map(([k,l,opts])=>(
            <F key={k} label={l}>
              <select value={d.cardio[k]} onChange={e=>un("cardio",k,e.target.value)} style={inp}>
                <option value="">Select...</option>{opts.map(o=><option key={o}>{o}</option>)}
              </select>
            </F>
          ))}
        </G4>

        <SH t="Respiratory" i="🫁" />
        <G3>
          <F label="Respiratory Effort">
            <select value={d.respiratorySystem.effort} onChange={e=>un("respiratorySystem","effort",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Unlabored","Mild increased work","Moderate distress","Severe distress","Apneic episodes"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Breathing Pattern">
            <select value={d.respiratorySystem.pattern} onChange={e=>un("respiratorySystem","pattern",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Regular","Irregular","Cheyne-Stokes","Kussmaul","Bradypnea","Tachypnea","Apneic"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="O2 Delivery (if applicable)">
            <select value={d.respiratorySystem.o2Delivery} onChange={e=>un("respiratorySystem","o2Delivery",e.target.value)} style={inp}>
              <option value="">Select / N/A...</option>
              {["Room air","NC — specify LPM","Simple mask","NRB mask","Venturi mask","HFO","Mechanical ventilation","Trach collar","T-piece"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>

        <SH t="Gastrointestinal" i="🔵" />
        <G4>
          {[["abdomen","Abdomen",["Soft, non-tender","Tender — specify in narrative","Distended","Rigid","Guarding"]],
            ["bowelSounds","Bowel Sounds",["Present all quadrants","Hypoactive","Hyperactive","Absent — MD notified"]],
            ["tube","Enteral Tube Present?",["No","Yes — G-tube","Yes — J-tube","Yes — NG","Yes — PEG"]],
            ["ostomy","Ostomy Present?",["No","Yes — Colostomy","Yes — Ileostomy","Yes — Urostomy"]]].map(([k,l,opts])=>(
            <F key={k} label={l}>
              <select value={d.gi[k]} onChange={e=>un("gi",k,e.target.value)} style={inp}>
                <option value="">Select...</option>{opts.map(o=><option key={o}>{o}</option>)}
              </select>
            </F>
          ))}
        </G4>

        <SH t="Genitourinary" i="🔵" />
        <G3>
          {[["bladder","Bladder/Voiding",["Continent","Incontinent — stress","Incontinent — urge","Incontinent — functional","Incontinent — complete"]],
            ["catheter","Urinary Catheter",["No","Yes — Foley","Yes — Suprapubic","Yes — Straight cath","Yes — External (condom)"]],
            ["urineColor","Urine Color (if observed)",["Not observed","Clear/pale yellow","Yellow","Dark amber","Cloudy","Hematuria","Strong odor noted"]]].map(([k,l,opts])=>(
            <F key={k} label={l}>
              <select value={d.gu[k]} onChange={e=>un("gu",k,e.target.value)} style={inp}>
                <option value="">Select...</option>{opts.map(o=><option key={o}>{o}</option>)}
              </select>
            </F>
          ))}
        </G3>

        <SH t="Integumentary" i="🩹" />
        <G4>
          {[["condition","Skin Integrity",["Intact","Skin tears present","Bruising noted","Rash present","Breakdown — see wound note"]],
            ["turgor","Skin Turgor",["Normal — returns immediately","Decreased — tents slightly","Poor — tents significantly"]],
            ["moisture","Skin Moisture",["Normal","Dry/flaking","Diaphoretic","Moist","Edematous"]],
            ["pressure","Pressure Ulcer Risk",["Low risk","Medium risk","High risk — prevention protocol initiated"]]].map(([k,l,opts])=>(
            <F key={k} label={l}>
              <select value={d.integument[k]} onChange={e=>un("integument",k,e.target.value)} style={inp}>
                <option value="">Select...</option>{opts.map(o=><option key={o}>{o}</option>)}
              </select>
            </F>
          ))}
        </G4>
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===3 && <>
        <SH t="ADL Functional Assessment" i="🏃" />
        <div style={{ border:`1.5px solid ${C.g200}`, borderRadius:10, overflow:"hidden", marginBottom:20 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", background:C.purple, color:C.white,
            padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase" }}>
            <span>Activity</span><span>Level of Assistance Required</span>
          </div>
          {ADLS.map(adl=>(
            <div key={adl} style={{ display:"grid", gridTemplateColumns:"1fr 2fr", padding:"8px 12px",
              borderBottom:`1px solid ${C.g100}`, alignItems:"center" }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.g800 }}>{adl}</span>
              <select value={d.adls[adl]||""} onChange={e=>setD(p=>({...p,adls:{...p.adls,[adl]:e.target.value}}))} style={{ ...inp, padding:"6px 10px", fontSize:12 }}>
                <option value="">Select...</option>
                {ADL_STATUS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>

        <SH t="Equipment in the Home" i="🏠" />
        <ChipGroup options={EQUIPMENT} value={d.functional.equipment} onChange={v=>setD(p=>({...p,functional:{...p.functional,equipment:v}}))} multi color={C.info} />

        <SH t="Home Safety Assessment" i="🔒" />
        <Notice type="gold">
          ACHC Standard HC2-1A requires environmental safety assessment at every skilled nursing visit.
          Document all hazards identified and interventions/education provided.
        </Notice>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:20 }}>
          {HOME_SAFETY.map(item=>(
            <label key={item} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 12px",
              borderRadius:8, border:`1.5px solid ${(d.homeSafety||[]).includes(item)?C.success:C.g200}`,
              background:(d.homeSafety||[]).includes(item)?"#F0FDF4":C.white, fontSize:13, transition:"all 0.15s" }}>
              <input type="checkbox" checked={(d.homeSafety||[]).includes(item)}
                onChange={e=>{ const curr=d.homeSafety||[]; u("homeSafety", e.target.checked ? [...curr,item] : curr.filter(x=>x!==item)); }}
                style={{ width:17, height:17, accentColor:C.success }} />
              <span style={{ color:C.g800 }}>{item}</span>
            </label>
          ))}
        </div>

        <SH t="Caregiver Assessment" i="👨‍👩‍👧" />
        <G3>
          <F label="Primary Caregiver Name"><input type="text" value={d.primaryCaregiver} onChange={e=>u("primaryCaregiver",e.target.value)} style={inp} /></F>
          <F label="Relationship">
            <select value={d.caregiverRelationship} onChange={e=>u("caregiverRelationship",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Spouse/Partner","Parent","Adult child","Sibling","Other family","Paid caregiver","Not applicable"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
          <F label="Caregiver Capacity">
            <select value={d.caregiverCapacity} onChange={e=>u("caregiverCapacity",e.target.value)} style={inp}>
              <option value="">Select...</option>
              {["Able and willing","Able — limited availability","Limited ability — needs support",
                "Unable — safety concern","No caregiver available"].map(v=><option key={v}>{v}</option>)}
            </select>
          </F>
        </G3>
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===4 && <>
        <SH t="Goals & Plan of Care" i="🎯" />
        <G2>
          <F label="Short-Term Goals (30-day)" req>
            <textarea value={d.shortTermGoals} onChange={e=>u("shortTermGoals",e.target.value)} style={{ ...inp, minHeight:80 }}
              placeholder="Measurable, time-bound, patient-centered goals..." />
          </F>
          <F label="Long-Term / Discharge Goals">
            <textarea value={d.longTermGoals} onChange={e=>u("longTermGoals",e.target.value)} style={{ ...inp, minHeight:80 }}
              placeholder="Expected patient outcomes and discharge criteria..." />
          </F>
        </G2>
        <G2>
          <F label="POC Changes Needed?">
            <ChipGroup options={["No","Yes — specify"]} value={d.pocChangesNeeded} onChange={v=>u("pocChangesNeeded",v)} />
            {d.pocChangesNeeded==="Yes — specify" && <textarea value={d.pocNotes} onChange={e=>u("pocNotes",e.target.value)} style={{ ...inp, minHeight:60, marginTop:8 }} placeholder="Describe required POC changes..." />}
          </F>
          <div>
            <F label="Follow-Up Required"><input type="text" value={d.followUpNeeded} onChange={e=>u("followUpNeeded",e.target.value)} style={inp} placeholder="e.g. RN visit in 3 days, wound care follow-up..." /></F>
            <F label="MD/NP Notified?"><YNSelect value={d.mdNotified} onChange={v=>u("mdNotified",v)} /></F>
          </div>
        </G2>
        <F label="Clinical Narrative (comprehensive)" mic="sn-narrative">
          <textarea id="sn-narrative" value={d.narrative} onChange={e=>u("narrative",e.target.value)} style={{ ...inp, minHeight:120, resize:"vertical" }}
            placeholder="Summarize assessment findings, changes from previous visit, patient/caregiver education provided and response, clinical reasoning for plan of care adjustments, and nursing impressions..." />
        </F>

        <PhotoUpload
          docId={null}
          formType="SKILLED_NURSING"
          onUploaded={(paths) =>
            setD(prev => ({ ...prev, photoPaths: paths }))}
        />

        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===5 && <>
        <SH t="Supporting Documents" i="📎" />
        <Notice type="blue">
          Attach hospital discharge summaries, prescriptions, MD orders,
          lab reports, voice recordings, or any other supporting documents
          relevant to this assessment. Each file requires a category.
        </Notice>
        <DocumentUpload
          docId={null}
          formType="SKILLED_NURSING"
          onUploaded={(docs) =>
            setD(prev => ({ ...prev, attachedDocuments: docs }))}
        />
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>setStep(step + 1)} />
      </>}

      {step===6 && <>
        <SH t="Signatures" i="✍️" />
        <Notice type="gold">
          <strong>ACHC & NC BON Requirement:</strong> Initial assessments must be signed by an RN.
          Per NC G.S. 90-171.20, reassessments may be signed by LPN with supervising RN co-signature if delegated.
          Client/representative signature confirms they were present and educated during the assessment.
        </Notice>
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <SigPad ref={rnSigPadRef} label="RN / CLINICIAN COMPLETING ASSESSMENT" signed={rnSig} onSigned={setRnSig} />
          <SigPad ref={clientSigPadRef} label={<>CLIENT / REPRESENTATIVE SIGNATURE <span style={{color:"#DC2626"}}>*</span></>}
            signed={clientSig} onSigned={setClientSig} />
        </div>
        {(!rnSig || !clientSig) && (
          <p style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>
            Both nurse and patient signatures required
          </p>
        )}
        <BtnRow onBack={()=>setStep(step - 1)} onNext={()=>{
            const rn = sigFromPad(rnSig, rnSigPadRef, "RN / CLINICIAN COMPLETING ASSESSMENT");
            const client = sigFromPad(clientSig, clientSigPadRef, "CLIENT / REPRESENTATIVE SIGNATURE");
            if (!rn || !client) return;
            onComplete({type:"SKILLED_NURSING",data:d,rnSig:rn,clientSig:client,patient,draftIdRef});
          }}
          nextLabel="✓ Submit Skilled Nursing Assessment" nextStyle={btnGold} disabled={!rnSig||!clientSig} />
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORM 6: NURSE'S SHIFT NOTE (per-visit internal documentation)
// Mirrors NNHC's paper Nurse's Shift Note used after every skilled nursing visit.
// ─────────────────────────────────────────────────────────────────────────────
export function NurseShiftNoteForm({ patient, onComplete, onSaveDraft, initialDraft, showToast }) {
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { url: dataUrl, ts: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke = rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type:"SHIFT_NOTE", data:d, lastStep:step, draftIdRef });
  };

  const today = new Date();
  const nextSunday = (() => {
    const days = (7 - today.getDay()) % 7 || 7;
    return new Date(today.getTime() + days * 86400000).toISOString().split("T")[0];
  })();

  const [d, setD] = useState(() => ({
    visitDate: today.toISOString().split("T")[0],
    weekEndingSunday: nextSunday,
    day: "",
    startTime: "",
    endTime: "",
    vitals: { temp: "", pulse: "", resp: "", o2sat: "" },
    pulseType: "RP",
    bp: "",
    bpPosition: "Sitting",
    mentalStatus: "AAO",
    mentalStatusOther: "",
    respiratoryStatus: { trach: false, vent: false, cpap: false, alarm: false, na: false },
    oxygen: "N/A",
    cardiacStatus: "WNL",
    edema: "",
    skinIntegrity: "intact W/D",
    neurological: "",
    seizures: false,
    seizureDetails: "",
    equipmentChecked: false,
    backupEquipmentChecked: false,
    emergencyEquipmentChecked: false,
    goBagChecked: false,
    otherNotes: "",
    nutritionDiet: "",
    bowelStatus: "",
    urinaryStatus: "",
    environmentSafety: "WNL",
    communicationNeeds: "MET",
    infectionControl: "Standard Precautions",
    infectionControlOther: "",
    changeInStatus: "No",
    coordinatedWith: [],
    coordinatedWithOther: "",
    addendumCompleted: false,
    painProblem: "No",
    painIntensity: "0",
    painType: "",
    painLocation: "",
    exacerbationFactors: "",
    painTreatment: [],
    painTreatmentOther: "",
    treatmentEffective: "",
    treatmentFollowUp: "",
    shiftNotes: "",
    progressNotes: "",
    mdNotes: "",
    ...(initialDraft?.form_data || {}),
  }));

  const u = (k, v) => setD(p => ({ ...p, [k]: v }));
  const uv = (k, v) => setD(p => ({ ...p, vitals: { ...p.vitals, [k]: v } }));
  const ur = (k, v) => setD(p => ({ ...p, respiratoryStatus: { ...p.respiratoryStatus, [k]: v } }));

  const totalTime = (() => {
    if (!d.startTime || !d.endTime) return "";
    const [sh, sm] = d.startTime.split(":").map(Number);
    const [eh, em] = d.endTime.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })();

  const DAYS = ["M", "T", "W", "TH", "F", "SA", "SU"];
  const COORD = ["N/A", "Clin Mgr", "RN/LPN", "Client Svc Mgr", "Physician", "DME", "Respiratory Therapist", "Other"];

  const Radio = ({ name, options, value, onChange, color }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map(o => (
        <label key={o} style={{
          padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
          background: value === o ? (color || C.purple) : C.g100,
          color: value === o ? C.white : C.g600,
          border: `1.5px solid ${value === o ? (color || C.purple) : C.g200}`,
        }}>
          <input type="radio" name={name} value={o} checked={value === o}
            onChange={() => onChange(o)} style={{ display: "none" }} />
          {o}
        </label>
      ))}
    </div>
  );

  const Check = ({ checked, onChange, label }) => (
    <label style={{
      display: "flex", alignItems: "center", gap: 8, padding: "7px 11px", borderRadius: 7,
      fontSize: 12, fontWeight: 500, cursor: "pointer",
      background: checked ? "#F0FDF4" : C.white,
      border: `1.5px solid ${checked ? C.success : C.g200}`,
      color: checked ? C.success : C.g600,
    }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 15, height: 15, accentColor: C.success }} />
      {label}
    </label>
  );

  const submitDisabled = !rnSig || !clientSig;

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 4 ? handleSaveDraftClick : () => onSaveDraft({ type:"SHIFT_NOTE", data:d, lastStep:step, draftIdRef })} />
      )}
      <Steps steps={["Visit & Vitals", "Systems Assessment", "Pain & Notes", "Signatures"]} current={step} onGo={setStep} />

      {step === 1 && <>
        <SH t="Visit Information" i="📅" />
        <G3>
          <F label="Visit Date" req>
            <input type="date" value={d.visitDate} onChange={e => u("visitDate", e.target.value)} style={inp} />
          </F>
          <F label="Week Ending (Sunday)">
            <input type="date" value={d.weekEndingSunday} onChange={e => u("weekEndingSunday", e.target.value)} style={inp} />
          </F>
          <F label="Total Time Worked">
            <input type="text" value={totalTime} readOnly style={{ ...inp, background: C.g100 }} placeholder="auto-calculated" />
          </F>
        </G3>
        <F label="Day" req>
          <Radio name="day" options={DAYS} value={d.day} onChange={v => u("day", v)} />
        </F>
        <G2>
          <F label="Start Time" req>
            <input type="time" value={d.startTime} onChange={e => u("startTime", e.target.value)} style={inp} />
          </F>
          <F label="End Time" req>
            <input type="time" value={d.endTime} onChange={e => u("endTime", e.target.value)} style={inp} />
          </F>
        </G2>

        <SH t="Vital Signs" i="🩺" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 16 }}>
          <F label="Temp °F" req><input type="number" step="0.1" value={d.vitals.temp} onChange={e => uv("temp", e.target.value)} style={inp} /></F>
          <F label="Pulse" req><input type="number" value={d.vitals.pulse} onChange={e => uv("pulse", e.target.value)} style={inp} /></F>
          <F label="Type">
            <Radio name="pt" options={["RP", "AP"]} value={d.pulseType} onChange={v => u("pulseType", v)} />
          </F>
          <F label="Resp" req><input type="number" value={d.vitals.resp} onChange={e => uv("resp", e.target.value)} style={inp} /></F>
          <F label="O2 Sat %" req><input type="number" value={d.vitals.o2sat} onChange={e => uv("o2sat", e.target.value)} style={inp} /></F>
          <F label="B/P" req><input type="text" value={d.bp} onChange={e => u("bp", e.target.value)} placeholder="126/68" style={inp} /></F>
          <F label="Position" req>
            <Radio name="bpp" options={["Sitting", "Lying", "Standing"]} value={d.bpPosition} onChange={v => u("bpPosition", v)} />
          </F>
        </div>
        <BtnRow onNext={() => setStep(2)} />
      </>}

      {step === 2 && <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* LEFT COLUMN */}
          <div>
            <F label="Mental Status" req>
              <Radio name="ms" options={["AAO", "Confused", "Disoriented", "Other"]} value={d.mentalStatus} onChange={v => u("mentalStatus", v)} />
              {d.mentalStatus === "Other" && (
                <input type="text" value={d.mentalStatusOther} onChange={e => u("mentalStatusOther", e.target.value)}
                  style={{ ...inp, marginTop: 8 }} placeholder="Specify..." />
              )}
            </F>
            <F label="Respiratory Status" req>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {[["trach", "Trach"], ["vent", "Vent"], ["cpap", "CPAP"], ["alarm", "Alarm"], ["na", "N/A"]].map(([k, l]) => (
                  <Check key={k} checked={d.respiratoryStatus[k]} onChange={v => ur(k, v)} label={l} />
                ))}
              </div>
            </F>
            <F label="Oxygen" req>
              <Radio name="ox" options={["Continuous", "PRN", "N/A"]} value={d.oxygen} onChange={v => u("oxygen", v)} />
            </F>
            <F label="Cardiac Status" req>
              <input type="text" value={d.cardiacStatus} onChange={e => u("cardiacStatus", e.target.value)} style={inp} />
            </F>
            <F label="Edema">
              <input type="text" value={d.edema} onChange={e => u("edema", e.target.value)} style={inp} placeholder="e.g. +2 Sacrum only" />
            </F>
            <F label="Skin Integrity" req>
              <input type="text" value={d.skinIntegrity} onChange={e => u("skinIntegrity", e.target.value)} style={inp} />
            </F>
            <F label="Neurological">
              <input type="text" value={d.neurological} onChange={e => u("neurological", e.target.value)} style={inp} placeholder="e.g. awake, alert" />
            </F>
            <F label="Seizures">
              <Check checked={d.seizures} onChange={v => u("seizures", v)} label="Seizure activity present" />
              {d.seizures && (
                <input type="text" value={d.seizureDetails} onChange={e => u("seizureDetails", e.target.value)}
                  style={{ ...inp, marginTop: 8 }} placeholder="e.g. Precautions" />
              )}
            </F>
            <F label="Equipment Verified">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <Check checked={d.equipmentChecked} onChange={v => u("equipmentChecked", v)} label="Primary" />
                <Check checked={d.backupEquipmentChecked} onChange={v => u("backupEquipmentChecked", v)} label="Backup" />
                <Check checked={d.emergencyEquipmentChecked} onChange={v => u("emergencyEquipmentChecked", v)} label="Emergency" />
                <Check checked={d.goBagChecked} onChange={v => u("goBagChecked", v)} label="Go Bag" />
              </div>
            </F>
            <F label="Other Notes (wound measurements, etc.)">
              <input type="text" value={d.otherNotes} onChange={e => u("otherNotes", e.target.value)} style={inp}
                placeholder="e.g. L 1.0 W 1.2 D 0.1 (L) Hip" />
            </F>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            <F label="Nutrition / Diet" req>
              <input type="text" value={d.nutritionDiet} onChange={e => u("nutritionDiet", e.target.value)} style={inp}
                placeholder="e.g. low Na high Protein" />
            </F>
            <F label="Bowel Status" req>
              <input type="text" value={d.bowelStatus} onChange={e => u("bowelStatus", e.target.value)} style={inp}
                placeholder="e.g. Formed several today" />
            </F>
            <F label="Urinary Status" req>
              <input type="text" value={d.urinaryStatus} onChange={e => u("urinaryStatus", e.target.value)} style={inp}
                placeholder="e.g. Suprapubic 16fr" />
            </F>
            <F label="Environment / Safety" req>
              <input type="text" value={d.environmentSafety} onChange={e => u("environmentSafety", e.target.value)} style={inp} />
            </F>
            <F label="Communication Needs" req>
              <input type="text" value={d.communicationNeeds} onChange={e => u("communicationNeeds", e.target.value)} style={inp} />
            </F>
            <F label="Infection Control" req>
              <Radio name="ic" options={["Standard Precautions", "Other"]} value={d.infectionControl} onChange={v => u("infectionControl", v)} />
              {d.infectionControl === "Other" && (
                <input type="text" value={d.infectionControlOther} onChange={e => u("infectionControlOther", e.target.value)}
                  style={{ ...inp, marginTop: 8 }} placeholder="Specify precautions..." />
              )}
            </F>
          </div>
        </div>

        <SH t="Change in Status" i="⚠️" />
        <F label="Was there a change in status?" req>
          <Radio name="cis" options={["Yes", "No"]} value={d.changeInStatus} onChange={v => u("changeInStatus", v)} />
        </F>
        {d.changeInStatus === "Yes" && <>
          <F label="Coordinated With (select all that apply)">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {COORD.map(c => (
                <Check key={c}
                  checked={(d.coordinatedWith || []).includes(c)}
                  onChange={v => {
                    const cur = d.coordinatedWith || [];
                    u("coordinatedWith", v ? [...cur, c] : cur.filter(x => x !== c));
                  }}
                  label={c} />
              ))}
            </div>
          </F>
          {(d.coordinatedWith || []).includes("Other") && (
            <F label="Other coordination">
              <input type="text" value={d.coordinatedWithOther} onChange={e => u("coordinatedWithOther", e.target.value)} style={inp} />
            </F>
          )}
          {(d.coordinatedWith || []).includes("Physician") && (
            <F label="Addendum">
              <Check checked={d.addendumCompleted} onChange={v => u("addendumCompleted", v)} label="Addendum completed" />
            </F>
          )}
        </>}

        <BtnRow onBack={() => setStep(1)} onNext={() => setStep(3)} />
      </>}

      {step === 3 && <>
        <SH t="Pain Status" i="😣" />
        <F label="Pain a problem this shift?" req>
          <Radio name="pp" options={["Yes", "No"]} value={d.painProblem} onChange={v => u("painProblem", v)} />
        </F>
        {d.painProblem === "Yes" && <>
          <G2>
            <F label="Pain Intensity (0–10)" req>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {[...Array(11)].map((_, i) => (
                  <div key={i} onClick={() => u("painIntensity", String(i))} style={{
                    width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center",
                    justifyContent: "center", cursor: "pointer", fontWeight: 700, fontSize: 12,
                    background: d.painIntensity === String(i) ? (i <= 3 ? C.success : i <= 6 ? C.warning : C.error) : C.g100,
                    color: d.painIntensity === String(i) ? C.white : C.g600,
                    border: `1.5px solid ${d.painIntensity === String(i) ? (i <= 3 ? C.success : i <= 6 ? C.warning : C.error) : C.g200}`,
                  }}>{i}</div>
                ))}
              </div>
            </F>
            <F label="Pain Type" req>
              <Radio name="pt2" options={["Recurrent", "New"]} value={d.painType} onChange={v => u("painType", v)} />
            </F>
          </G2>
          {d.painType === "New" && (
            <F label="Pain Location">
              <input type="text" value={d.painLocation} onChange={e => u("painLocation", e.target.value)} style={inp} placeholder="e.g. left heel" />
            </F>
          )}
          <F label="Exacerbation Factors">
            <input type="text" value={d.exacerbationFactors} onChange={e => u("exacerbationFactors", e.target.value)} style={inp}
              placeholder="e.g. pressure, stimulation, tactile" />
          </F>
          <F label="Pain Treatment" req>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {["Medication", "Rest", "Other"].map(t => (
                <Check key={t}
                  checked={(d.painTreatment || []).includes(t)}
                  onChange={v => {
                    const cur = d.painTreatment || [];
                    u("painTreatment", v ? [...cur, t] : cur.filter(x => x !== t));
                  }}
                  label={t} />
              ))}
            </div>
            {(d.painTreatment || []).includes("Other") && (
              <input type="text" value={d.painTreatmentOther} onChange={e => u("painTreatmentOther", e.target.value)}
                style={{ ...inp, marginTop: 8 }} placeholder="Specify other treatment..." />
            )}
          </F>
          <F label="Treatment Effective?" req>
            <Radio name="te" options={["Yes", "No"]} value={d.treatmentEffective} onChange={v => u("treatmentEffective", v)} />
          </F>
          {d.treatmentEffective === "No" && (
            <F label="Intervention / Follow-up">
              <input type="text" value={d.treatmentFollowUp} onChange={e => u("treatmentFollowUp", e.target.value)} style={inp} />
            </F>
          )}
        </>}

        <SH t="Shift Notes" i="📝" />
        <F label="Shift Notes" req>
          <textarea value={d.shiftNotes} onChange={e => u("shiftNotes", e.target.value)}
            style={{ ...inp, minHeight: 130, resize: "vertical" }}
            placeholder="Document initial observation, interventions performed, patient response, wound measurements if applicable..." />
        </F>

        <SH t="Progress Toward Goals" i="🎯" />
        <F label="Progress Notes">
          <textarea value={d.progressNotes} onChange={e => u("progressNotes", e.target.value)}
            style={{ ...inp, minHeight: 80, resize: "vertical" }}
            placeholder="Progress toward goals/outcomes of the shift..." />
        </F>

        <SH t="Multidisciplinary Notes" i="🤝" />
        <F label="MD / Multidisciplinary Notes">
          <textarea value={d.mdNotes} onChange={e => u("mdNotes", e.target.value)}
            style={{ ...inp, minHeight: 80, resize: "vertical" }}
            placeholder="Multidisciplinary notes..." />
        </F>

        <BtnRow onBack={() => setStep(2)} onNext={() => setStep(4)} />
      </>}

      {step === 4 && <>
        <SH t="Signatures" i="✍️" />
        <Notice type="gold">
          <strong>NNHC Internal Documentation:</strong> Nurse's Shift Note is completed at the
          end of every skilled nursing visit. Both clinician and client signatures required per
          ESIGN Act and NC UETA.
        </Notice>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <SigPad ref={rnSigPadRef} label="RN / LPN CLINICIAN SIGNATURE" signed={rnSig} onSigned={setRnSig} />
          <SigPad ref={clientSigPadRef} label={<>PATIENT / CLIENT SIGNATURE <span style={{color:"#DC2626"}}>*</span></>}
            signed={clientSig} onSigned={setClientSig} />
        </div>
        {(!rnSig || !clientSig) && (
          <p style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>
            Both nurse and patient signatures required
          </p>
        )}
        <BtnRow onBack={() => setStep(3)}
          onNext={() => {
            const rn = sigFromPad(rnSig, rnSigPadRef, "RN / LPN CLINICIAN SIGNATURE");
            const client = sigFromPad(clientSig, clientSigPadRef, "PATIENT / CLIENT SIGNATURE");
            if (!rn || !client) return;
            onComplete({
              type: "SHIFT_NOTE",
              data: { ...d, totalTimeWorked: totalTime },
              rnSig: rn, clientSig: client, patient, draftIdRef,
            });
          }}
          nextLabel="✓ Submit Shift Note" nextStyle={btnGold} disabled={submitDisabled} />
      </>}
    </div>
  );
}
